export const SUPPORTED_LANGUAGES = [
  { value: "en", label: "English", nativeName: "English", locale: "en" },
  { value: "de", label: "German", nativeName: "Deutsch", locale: "de" },
] as const;

export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number]["value"];

export const DEFAULT_LANGUAGE: AppLanguage = "en";

export function isAppLanguage(value: unknown): value is AppLanguage {
  return SUPPORTED_LANGUAGES.some((language) => language.value === value);
}

export function localeForLanguage(language: AppLanguage): string {
  return SUPPORTED_LANGUAGES.find((item) => item.value === language)?.locale ?? DEFAULT_LANGUAGE;
}

const EN = {
  "common.account": "Account",
  "common.add": "Add",
  "common.advanced": "Advanced",
  "common.allDay": "All day",
  "common.allTasks": "All tasks",
  "common.back": "Back",
  "common.backToSettings": "Back to settings",
  "common.backup": "Backup",
  "common.build": "Build",
  "common.calendar": "Calendar",
  "common.calendars": "Calendars",
  "common.cancel": "Cancel",
  "common.claude": "Claude",
  "common.clear": "Clear",
  "common.clearAll": "Clear all",
  "common.closeDialog": "Close dialog",
  "common.color": "Color",
  "common.complete": "Complete",
  "common.create": "Create",
  "common.day": "Day",
  "common.days": "Days",
  "common.delete": "Delete",
  "common.default": "Default",
  "common.developer": "Developer",
  "common.done": "Done",
  "common.edit": "Edit",
  "common.event": "Event",
  "common.events": "Events",
  "common.export": "Export",
  "common.feedback": "Feedback",
  "common.filters": "Filters",
  "common.hide": "Hide",
  "common.hidden": "Hidden",
  "common.import": "Import",
  "common.inbox": "Inbox",
  "common.language": "Language",
  "common.lists": "Lists",
  "common.labels": "Labels",
  "common.loading": "Loading...",
  "common.month": "Month",
  "common.navigation": "Navigation",
  "common.new": "New",
  "common.newTask": "New task",
  "common.next": "Next",
  "common.noDate": "No date",
  "common.none": "none",
  "common.notes": "Notes",
  "common.off": "Off",
  "common.on": "On",
  "common.previous": "Previous",
  "common.moreInfo": "More information",
  "common.remove": "Remove",
  "common.reset": "Reset",
  "common.restore": "Restore",
  "common.save": "Save",
  "common.search": "Search",
  "common.selectAll": "Select all",
  "common.settings": "Settings",
  "common.show": "Show",
  "common.shown": "Shown",
  "common.sync": "Sync",
  "common.task": "Task",
  "common.tasks": "Tasks",
  "common.today": "Today",
  "common.topics": "Topics",
  "common.trash": "Trash",
  "common.version": "Version",
  "common.week": "Week",

  "nav.main": "Main",
  "nav.title": "Calendar",

  "settings.category.general": "General",
  "settings.category.general.blurb": "Language, time, region, startup",
  "settings.category.appearance": "Appearance",
  "settings.category.appearance.blurb": "Theme, text, block color",
  "settings.category.theme": "UI Colors",
  "settings.category.theme.blurb": "Colors, palettes, presets",
  "theme.title": "Theme",
  "theme.preset": "Theme preset",
  "theme.preset.description": "Pick a starting theme. Tweak any part below; use Revert to restore a change.",
  "theme.custom": "Custom",
  "theme.source": "Source color",
  "theme.source.description": "The whole color scheme is generated from this color.",
  "theme.sourceLight": "Light source",
  "theme.sourceDark": "Dark source (optional)",
  "theme.sourceRevert": "Revert to preset color",
  "theme.variant": "Style",
  "theme.revertAll": "Revert to preset",
  "theme.syncTitle": "Sync theme across devices",
  "theme.syncDescription": "Share this theme with your other signed-in devices. If two devices differ, you'll be asked which to keep.",
  "theme.roles": "Generated UI colors",
  "theme.roles.description": "Every interface color is generated from the source. Override any of them; Revert restores the generated value.",
  "theme.entityColors": "Default colors",
  "theme.entityColors.description": "Broad fallback colors for entries — pick them from the color board above. Per-topic, per-calendar and per-label colors live under Settings → Topics, Labels and Calendars.",
  "theme.uiColors": "Interface colors",
  "theme.uiColors.description": "Text, menus and surfaces — generated from the source color, independent of the color board.",
  "theme.entity": "Set colors by",
  "theme.entity.description": "Everything, tasks, or events — topic, calendar and label colors (set under Topics, Labels and Calendars) win over these.",
  "theme.entity.global": "Everything",
  "theme.entity.task": "Tasks",
  "theme.entity.event": "Events",
  "theme.paletteBoard": "Color board",
  "theme.paletteBoard.applyTo": "Painting",
  "theme.paletteBoard.imageSample": "Image sample",
  "theme.paletteBoard.debug": "Debug",
  "theme.paletteBoard.slot": "Palette {n}",
  "theme.paletteBoard.hint": "Click any color below to select it, then click the board to paint it — swatches pick exact palette colors, the artwork samples freely.",
  "theme.preset.change": "Change theme",
  "theme.preset.hide": "Hide",
  "theme.preview": "Preview",
  "theme.preview.day": "Day view",
  "theme.preview.overview": "Overview",
  "theme.mode.light": "Light",
  "theme.mode.dark": "Dark",
  "theme.vibrancy": "Vibrancy",
  "theme.vibrancy.description": "Boost or mute the saturation of every topic & calendar color at once.",
  "theme.autoColorCalendars": "Auto-color calendars",
  "theme.conflict.title": "Theme changed on another device",
  "theme.conflict.body": "This device and another have different themes. Keep this device's theme, or use the synced one?",
  "theme.conflict.keep": "Keep this device",
  "theme.conflict.override": "Use synced theme",
  "theme.variant.tonal_spot": "Tonal",
  "theme.variant.vibrant": "Vibrant",
  "theme.variant.expressive": "Expressive",
  "theme.variant.content": "Content",
  "theme.variant.neutral": "Neutral",
  "settings.category.tasks": "Tasks & calendar",
  "settings.category.tasks.blurb": "New-entry defaults, reminders, subtasks, editor",
  "settings.category.navigation": "Navigation",
  "settings.category.navigation.blurb": "Sidebar items and keyboard shortcuts",
  "settings.category.navigation.mobileBlurb": "Sidebar items",
  "settings.category.organize": "Topics, Labels and Calendars",
  "settings.category.organize.blurb": "Topics, calendars, labels — colors, order, visibility",
  "settings.category.sync": "Sync & accounts",
  "settings.category.sync.blurb": "Account, multi-device sync, Google",
  "settings.category.data": "Data & privacy",
  "settings.category.data.blurb": "API key, import/export, backup",
  "settings.category.trash": "Trash",
  "settings.category.trash.blurb": "Restore or permanently delete items",
  "settings.category.about": "About & feedback",
  "settings.category.about.blurb": "Version, feedback, developer options",
  "settings.category.developer": "Developer",
  "settings.category.developer.blurb": "Diagnostics, debug flags, error log",
  "settings.search.placeholder": "Search settings...",
  "settings.search.none": "No matching settings.",
  "settings.resetAria": "Reset {title} to default",

  "general.appearance": "Appearance",
  "general.languageTime": "Language & time",
  "appearance.display": "Theme & color",
  "appearance.text": "Text",
  "general.theme": "Theme",
  "general.theme.light": "Light",
  "general.theme.dark": "Dark",
  "general.theme.system": "System",
  "general.blockColor": "Block color",
  "general.blockColor.description": "Calendar and inbox blocks fill with the task's color, just like the editor. Drag to set how much lighter tasks render versus events; a task with both a topic and a custom color blends the two.",
  "general.blockColor.aria": "Task vs event color difference",
  "general.blockColor.gradient": "Gradient on single colors",
  "general.blockColor.task": "Task",
  "general.blockColor.event": "Event",
  "general.blockColor.mix": "Mix",
  "general.display": "Display",
  "general.language.description": "Controls localized app surfaces. English and German are supported now.",
  "general.timeFormat": "Time format",
  "general.timeFormat.24": "24-hour",
  "general.timeFormat.12": "12-hour",
  "general.textSize": "Text size",
  "general.textSize.description": "Scales app text while keeping headings, labels and dense calendar text in proportion.",
  "general.textSize.aria": "App text size",
  "general.textSize.small": "Small",
  "general.textSize.large": "Large",
  "general.textSize.previewTitle": "Review tomorrow's plan",
  "general.textSize.previewBody": "Headings, body copy and compact labels keep their own rhythm.",
  "general.textFont": "Text font",
  "general.textFont.description": "Choose the main font used across the app.",
  "general.textFont.system": "System",
  "general.textFont.rounded": "Rounded",
  "general.textFont.serif": "Serif",
  "general.textFont.mono": "Mono",
  "general.startup": "Startup",
  "general.defaultView": "Default view",
  "general.defaultView.description": "Where the app opens on launch.",
  "general.multiDayFocus": "Week & X-Days focus",
  "general.multiDayFocus.description": "Choose the leftmost visible day when Week or X-Days focuses the current period. Current week follows your Week starts on setting.",
  "general.multiDayFocus.currentWeek": "Current week",
  "general.multiDayFocus.currentDay": "Current day",
  "general.multiDayFocus.previousDay": "Current day − 1",
  "general.reset": "Reset",
  "general.resetPrefs": "Reset all preferences",
  "general.resetPrefs.description": "Restore every preference (appearance, defaults, snooze, etc.) to its default. Your tasks, calendars, topics, labels, account and sync settings are NOT affected.",
  "general.resetPrefs.confirm": "Yes, reset preferences",
  "general.resetPrefs.button": "Reset all preferences...",
  "general.resetPrefs.done": "Preferences reset to defaults.",
  "general.showOnboarding": "Setup guide",
  "general.showOnboarding.description": "Reopen the first-run setup: account, notifications, import, theme, organization and time settings.",
  "general.showOnboarding.button": "Open setup guide",

  "defaults.newEntries": "New entries",
  "defaults.newEntries.description": "What a freshly-created task or event starts as.",
  "defaults.defaultCalendar": "Default calendar",
  "defaults.defaultCalendar.description": "New entries are filed here unless you pick another.",
  "defaults.noCalendars": "(no calendars)",
  "defaults.defaultType": "Default entry type",
  "defaults.defaultType.description": "Whether the create form opens as an event or a task.",
  "defaults.defaultDuration": "Default event length",
  "defaults.defaultDuration.description": "Length of a new timed entry created from the + button (drag-to-create uses the dragged span).",
  "defaults.customDurationAria": "Custom default length in minutes",
  "defaults.timing": "Timing",
  "defaults.defaultTz": "Default time zone",
  "defaults.defaultTz.description": "Applied to new tasks. Pick a different zone per-task under the editor's Advanced options.",
  "defaults.weekStart": "Week starts on",
  "defaults.sunday": "Sunday",
  "defaults.monday": "Monday",
  "defaults.reminders.description": "Seeded onto new entries; change them per-entry in the editor.",
  "defaults.taskReminders": "Default task reminders",
  "defaults.eventReminders": "Default event reminders",
  "defaults.eventReminders.description": "Events default to no reminder; add lead times to remind for new events too.",
  "defaults.snoozeAnchors": "Snooze anchors",
  "defaults.snoozeAnchors.description": "The hours the snooze presets anchor to. \"Tomorrow morning\" / \"This weekend\" / \"Next week\" use the morning hour; \"This evening\" uses the evening hour. The Priorities view's scheduling shortcuts use all three.",
  "defaults.morning": "Morning",
  "defaults.afternoon": "Afternoon",
  "defaults.evening": "Evening",
  "defaults.snoozePreview": "Tomorrow morning -> {tomorrow} · This evening -> {evening}",

  "taskSettings.remindersSnooze": "Reminders & snooze",
  "taskSettings.subtasks": "Subtasks",
  "taskSettings.todayView": "Today view",
  "taskSettings.agendaDays": "Agenda length",
  "taskSettings.agendaDays.description": "How many days the Today screen looks ahead, and whether days with nothing scheduled still show.",
  "taskSettings.agendaDays.option": "{count} days",
  "taskSettings.agendaShowEmpty": "Empty days",
  "taskSettings.subtaskCompletion": "Subtask completion",
  "taskSettings.subtaskCompletion.description": "How completing a task and its subtasks affect each other.",
  "taskSettings.rollup.twoWay": "Two-way",
  "taskSettings.rollup.childrenToParent": "Children -> Parent",
  "taskSettings.rollup.parentToChildren": "Parent -> Children",
  "taskSettings.rollup.ask": "Ask each time",
  "taskSettings.editor": "Editor",
  "taskSettings.matrixScope": "3×3 Eisenhower matrix (priority/urgency)",
  "taskSettings.matrixScope.description": "Where the 3×3 priority × urgency matrix is available. Outside its scope the forms use the classic priority flags and blocks keep flag colors.",
  "taskSettings.matrixScope.off": "Don't enable",
  "taskSettings.matrixScope.tasks": "Only for tasks",
  "taskSettings.matrixScope.tasksEvents": "Tasks & events",
  "taskSettings.deadlineUrgency": "Urgency from deadline",
  "taskSettings.deadlineUrgency.description": "When you haven't placed a task on the matrix yourself, fill its urgency automatically from how close its deadline is (falling back to the scheduled date). Turn off to leave auto-urgency blank until you set it.",
  "taskSettings.deadlineUrgency.on": "Auto",
  "taskSettings.deadlineUrgency.off": "Off",
  "taskSettings.deadlineUrgency.highDays": "High within (days)",
  "taskSettings.deadlineUrgency.mediumDays": "Medium within (days)",
  "taskSettings.deadlineUrgency.thresholdHint": "≤ {high} days → high · ≤ {medium} days → medium · beyond → low. Today counts as 0 days; overdue counts as high.",
  "taskSettings.desktopFormVariant": "Editor style (desktop)",
  "taskSettings.desktopFormVariant.description": "How the create/edit window looks on desktop. Paper is the classic manuscript card; Modern is the new look that follows the mobile design — same layout, fields and animations.",
  "taskSettings.desktopFormVariant.paper": "Paper (classic)",
  "taskSettings.desktopFormVariant.modern": "Modern (new)",
  "taskSettings.topicEditor": "Create/edit topics",
  "taskSettings.topicEditor.description": "Topic buttons in the touch sheet can show icons, text, or both. The topics-per-task choice also applies to the desktop/web editor.",
  "taskSettings.topicButtons": "Topic buttons",
  "taskSettings.iconsText": "Icons + text",
  "taskSettings.icons": "Icons",
  "taskSettings.text": "Text",
  "taskSettings.topicsPerTask": "Topics per task",
  "taskSettings.multiple": "Multiple",
  "taskSettings.single": "Single",
  "taskSettings.mobile": "Mobile",
  "taskSettings.swipeHint": "Calendar swipe hint",
  "taskSettings.swipeHint.description": "Show a small hint bar at the top of calendar views reminding you to swipe left for Filters and right for Inbox. Hide it once you know the gestures.",

  "navigation.sidebar": "Sidebar",
  "navigation.showInSidebar": "Show in sidebar",
  "navigation.showInSidebar.description": "Hide parts of the sidebar you don't use. (The \"YourCalendar\" title always links Home.)",
  "navigation.keyboardShortcuts": "Keyboard shortcuts",
  "navigation.part.today": "Today",
  "navigation.part.today.desc": "The Today nav item.",
  "navigation.part.calendar": "Calendar",
  "navigation.part.calendar.desc": "The Calendar nav item.",
  "navigation.part.tasks": "All tasks",
  "navigation.part.tasks.desc": "The All-tasks nav item.",
  "navigation.part.priorities": "Priorities",
  "navigation.part.priorities.desc": "The importance × urgency decision view. Automatically shown while the matrix is enabled, unless you override it.",
  "navigation.useAutomatic": "Use automatic",
  "navigation.part.inbox": "Inbox",
  "navigation.part.inbox.desc": "The undated-backlog nav item.",
  "navigation.part.search": "Search",
  "navigation.part.search.desc": "The search nav item.",
  "navigation.part.claude": "Claude",
  "navigation.part.claude.desc": "The AI nav item.",
  "navigation.part.filters": "Filters",
  "navigation.part.filters.desc": "The Topics / Labels / Priority / Calendars group.",

  "shortcuts.title": "Keyboard shortcuts",
  "shortcuts.description": "Click a key, then press the new key. Esc cancels.",
  "shortcuts.newTask": "New task",
  "shortcuts.today": "Go to Today",
  "shortcuts.priorities": "Go to Priorities",
  "shortcuts.calNext": "Calendar: next period",
  "shortcuts.calPrev": "Calendar: previous period",
  "shortcuts.help": "Show shortcuts help",
  "shortcuts.undo": "Undo last change",
  "shortcuts.rebind": "Rebind {desc}",
  "shortcuts.pressKey": "Press a key...",
  "shortcuts.duplicate": "Two actions share a key -- give each a unique key.",
  "shortcuts.showThisHelp": "Show this help",
  "shortcuts.rebindHint": "Rebind these in Settings -> Navigation.",

  "data.integrations": "Integrations",
  "data.apiKey": "Anthropic API key",
  "data.apiKey.description": "Stored locally on this device. Required for Claude features.",
  "data.apiKey.saved": "An API key is saved on this device.",
  "data.apiKey.savedPlaceholder": "Enter a new key to replace the saved one",
  "data.importExport": "Import & export",
  "data.ics": "Import / export calendar (.ics)",
  "data.ics.description": "Exchange tasks, events & recurrence with other calendar apps as a standard iCalendar file. Export lets you choose calendars; import (one or more files) merges into a calendar you pick and skips anything already present.",
  "data.exportIcs": "Export .ics",
  "data.importIcs": "Import .ics",
  "data.settingsFile": "Export / import settings",
  "data.settingsFile.description": "Save just your preferences (theme, defaults, sidebar, etc.) to a file, or load them on another device. Never includes your API key, account or tasks.",
  "data.exportSettings": "Export settings",
  "data.importSettings": "Import settings",
  "data.backupRestore": "Backup & restore",
  "data.backupRestore.description": "Save everything (tasks, events, lists and preferences -- never your API key) to a file, load it back, or wipe all data on this device. Load lets you pick categories and merge vs replace.",
  "data.saveToFile": "Save to file",
  "data.loadFromFile": "Load from file",
  "data.deleteAllData": "Delete all data",
  "data.restore.clearing": "Clearing existing data... {percent}%",
  "data.restore.writing": "Restoring... {percent}%",

  "storage.fallback.warning": "Your saved data could not be opened, so the app started empty. Nothing has been deleted -- please restart the app before adding anything, and report this if it happens again.",

  "lists.calendars.description": "Each task belongs to one calendar; hidden calendars are filtered off the views. The default is assigned to new tasks (changeable per-task under the editor's Advanced options).",
  "lists.makeDefault": "Make this the default for new tasks",
  "lists.hiddenTitle": "Hidden from views -- click to show",
  "lists.shownTitle": "Shown on views -- click to hide",
  "lists.onlyCalendarTitle": "Can't delete the only calendar",
  "lists.deleteCalendarTitle": "Delete calendar",
  "lists.newCalendar": "New calendar name",
  "lists.deleteNamed": "Delete \"{name}\"?",
  "lists.deleteCalendarWithTasks": "This permanently deletes the calendar and its {count} {noun} (including recurring series). This cannot be undone.",
  "lists.deleteCalendarEmpty": "This calendar has no tasks. Delete it?",
  "lists.deleteCalendarAndTasks": "Delete calendar and {count} {noun}",
  "lists.topics.description": "Nested groupings you can assign to tasks (a task may have several). Set an icon (emoji) and color per topic -- both show on the task form's topic tabs. Drag a row's grip handle right to nest it under the topic above, or left to promote it back out. Deleting one promotes its sub-topics to its parent.",
  "lists.topicIcon": "{name} icon",
  "lists.topicIconTitle": "Icon (emoji) shown on the task form's topic tab -- click to change",
  "lists.deleteTopicTitle": "Delete topic",
  "lists.newTopic": "New topic name",
  "lists.deleteTopicBody": "Any sub-topics are promoted to this topic's parent. Tasks tagged with it will no longer show it. This cannot be undone.",
  "lists.labels.description": "Cross-cutting tags you can assign to tasks (a task may have several) and filter by from the sidebar. Distinct from Topics, which are nested groupings.",
  "lists.deleteLabelTitle": "Delete label",
  "lists.newLabel": "New label name",
  "lists.deleteLabelBody": "Tasks tagged with it will no longer show it. This cannot be undone.",

  "organize.showIn": "Show in",
  "organize.showIn.month": "Month",
  "organize.showIn.today": "Today",
  "organize.showIn.priorities": "Priorities",
  "organize.showIn.shownTitle": "Shown in {view} — click to hide it there",
  "organize.showIn.hiddenTitle": "Hidden from {view} — click to show it there",
  "organize.globallyHidden": "globally hidden",
  "organize.expandRow": "Show view options for {name}",
  "organize.collapseRow": "Hide view options for {name}",
  "organize.dragRow": "Drag to reorder",
  "organize.paletteNumber": "#{n} — this number marks its color on the palette board",
  "organize.colorTap": "Click, then pick a color from the palette board above",
  "organize.colorTapNamed": "{name} color -- click, then pick a color from the palette board above",
  "organize.colorArmed": "Pick on board",
  "organize.colorAuto": "Auto",
  "organize.colorCustom": "Custom",
  "organize.colorRevert": "Revert to automatic color",

  "emoji.pick": "Choose an emoji",
  "emoji.pickNamed": "Choose an emoji for {name}",
  "emoji.search": "Search emoji in any language…",
  "emoji.frequent": "Suggested",
  "emoji.none": "No results",
  "emoji.remove": "Remove emoji",

  "backup.deleteAllShort": "Delete all",
  "backup.deleteAllTitle": "Delete all data",
  "backup.deleteAllBody": "This permanently deletes every task, event, recurring series, label, topic and calendar. Your settings (API key, preferences) are kept, and a fresh default calendar is created. This cannot be undone.",
  "backup.deleteEverything": "Delete everything",
  "backup.importTitle": "Load from file",
  "backup.importDescription": "Choose what to bring in from this backup.",
  "backup.settingsPreferences": "Settings & preferences",
  "backup.none": "none",
  "backup.merge": "Merge -- add only the checked items not already present",
  "backup.replace": "Replace -- overwrite the checked categories (others untouched)",

  "ics.exportTitle": "Export calendar",
  "ics.exportDescription": "Choose which calendars to include in the .ics file.",
  "ics.exportButton": "Export {count} {noun}",
  "ics.importTitle": "Import calendar",
  "ics.importSummary": "{count} {itemNoun} from the chosen {fileLabel}. Items already present (matched by UID) are skipped.",
  "ics.file": "file",
  "ics.files": "{count} files",
  "ics.item": "item",
  "ics.items": "items",
  "ics.createNewCalendar": "Create a new calendar",
  "ics.calendarName": "Calendar name",
  "ics.addExistingCalendar": "Add to an existing calendar",

  "feedback.captureUnavailable": "Screen capture is not available in this browser.",
  "feedback.captureFailed": "Could not capture the screen.",
  "feedback.readyToShare": "Feedback ready to share. Send it to {email}.",
  "feedback.shareTabOpened": "Feedback tab opened with the screenshot and email steps.",
  "feedback.emailDraftOpened": "Email draft opened.",
  "feedback.openFailed": "Could not open feedback sharing.",
  "feedback.notes": "Notes",
  "feedback.placeholder": "What happened? What did you expect?",
  "feedback.removeImage": "Remove",
  "feedback.previewAlt": "Feedback attachment preview",
  "feedback.attachmentHint": "Attach a screenshot or image when it helps explain the issue.",
  "feedback.capturing": "Capturing...",
  "feedback.captureScreen": "Capture screen",
  "feedback.attachImage": "Attach image",
  "feedback.opening": "Opening...",
  "feedback.footer": "Feedback is addressed to {email}. Your mail or share app will ask before anything is sent.",
  "feedback.disabled": "Screenshot feedback popup disabled.",
  "feedback.hideAfterScreenshots": "Don't show this after screenshots",
  "feedback.subject": "YourCalendar app feedback",
  "feedback.noNote": "(No note provided.)",
  "feedback.openingTitle": "Opening feedback email",
  "feedback.openingBody": "Opening your mail app...",
  "feedback.composeTabTitle": "Send YourCalendar Feedback",
  "feedback.composeTabHeading": "Send YourCalendar feedback",
  "feedback.composeTabIntro": "Your browser could not attach the screenshot automatically. Use this tab to copy the message and screenshot, then send them to {email}.",
  "feedback.openEmailApp": "Open email app",
  "feedback.copyMessage": "Copy message",
  "feedback.copyScreenshot": "Copy screenshot",
  "feedback.message": "Message",
  "feedback.messageHelp": "The email app button fills the recipient, subject, and message. If your mail app opens without the screenshot, copy the screenshot here and paste or attach it there.",
  "feedback.screenshot": "Screenshot",
  "feedback.screenshotAlt": "Feedback screenshot",
  "feedback.statusEmailOpened": "Email app opened. Paste or attach the screenshot if it was not included automatically.",
  "feedback.statusMessageCopied": "Message copied.",
  "feedback.statusMessageSelect": "Select the message text and copy it.",
  "feedback.statusScreenshotCopied": "Screenshot copied. Paste it into your email.",
  "feedback.statusScreenshotCopyFailed": "Could not copy the screenshot. Right-click the screenshot and copy or save it, then attach it to the email.",

  "about.about": "About",
  "about.sendFeedback": "Send feedback",
  "about.sendFeedback.description": "Send a note and optional image to {email}. On mobile, system screenshots can open this composer automatically.",
  "about.screenshotFeedback": "Screenshot feedback",
  "about.screenshotFeedback.description": "On mobile, taking a system screenshot can open the feedback composer with the screenshot attached.",
  "about.advanced": "Advanced",
  "about.developerOptions": "Developer options",
  "about.developerOptions.description": "Unlock the Developer category -- diagnostics, an error log, debug logging toggles and calendar-pan tuning. Off by default; you don't need these for normal use.",
  "about.developerUnlocked": "Developer options unlocked -- see the Developer category.",

  "calendar.previousUnit": "Previous {unit}",
  "calendar.nextUnit": "Next {unit}",
  "calendar.swipeFilters": "<- Filters",
  "calendar.swipeInbox": "Inbox ->",
  "calendar.slideToExpand": "(slide to expand)",
  "calendar.newEvent": "New event",
  "calendar.fewerDays": "Fewer days",
  "calendar.moreDays": "More days",
  "calendar.dayCount": "{count} Days",

  "sidebar.hide": "Hide {label}",
  "sidebar.settings": "Sidebar settings",
  "sidebar.expand": "Expand {title}",
  "sidebar.collapse": "Collapse {title}",
  "sidebar.dragSection": "Drag to reorder section",
  "sidebar.manage": "Manage",
  "sidebar.manageTitle": "Manage {title}",
  "sidebar.clear": "Clear",
  "sidebar.any": "any",
  "sidebar.all": "all",
  "sidebar.matchAny": "Match any selected {noun}",
  "sidebar.matchAll": "Match all selected {noun}s",
  "sidebar.label": "label",
  "sidebar.topic": "topic",
  "sidebar.priority": "Priority",
  "sidebar.priority.high": "High",
  "sidebar.priority.medium": "Medium",
  "sidebar.priority.low": "Low",
  "sidebar.priority.none": "None",
  "matrix.title": "Priority matrix",
  "matrix.importance": "Importance",
  "matrix.urgency": "Urgency",
  "matrix.low": "low",
  "matrix.high": "high",
  "matrix.hint": "Hold to fine-tune",
  "matrix.clear": "Clear",
  "matrix.cellLabel": "{priority} importance, {urgency} urgency",
  "matrix.axisOption": "{axis}: {level}",
  "matrix.autoUrgency": "Auto",
  "matrix.autoUrgency.hint": "Urgency follows the deadline automatically.",
  "matrix.manualUrgency": "Manual",
  "matrix.manualUrgency.hint": "Urgency stays where you place it.",
  "matrix.priorityFromMatrix": "Priority follows the matrix",
  "matrix.hidden.off": "On board",
  "matrix.hidden.on": "Hidden",
  "matrix.hidden.hide.hint": "Hide from the Priorities board — for the whole series. It still occupies time on the calendar and the day load.",
  "matrix.hidden.show.hint": "Show on the Priorities board again — for the whole series.",

  "priorities.title": "Priorities",
  "priorities.horizon.label": "Time range",
  "priorities.horizon.today": "Today",
  "priorities.horizon.d3": "3 days",
  "priorities.horizon.d7": "Week",
  "priorities.horizon.d14": "2 weeks",
  "priorities.horizon.d30": "Month",
  "priorities.horizon.all": "All",
  "priorities.include.overdue": "Overdue",
  "priorities.include.undated": "No date",
  "priorities.include.done": "Completed",
  "priorities.include.events": "Events",
  "priorities.summary": "{count} open · {time} planned",
  "priorities.summaryNoTime": "{count} open",
  "priorities.corner.doNow": "Do now",
  "priorities.corner.schedule": "Plan time for it",
  "priorities.corner.squeeze": "Squeeze in",
  "priorities.corner.later": "Maybe later",
  "priorities.board.hint": "Drag a task to change its priority — click to open it.",
  "priorities.board.empty": "No tasks in this range.",
  "priorities.tray.title": "Not placed yet ({count})",
  "priorities.tray.hint": "Drag onto the board to rank these.",
  "priorities.cluster.more": "+{count} more",
  "priorities.cluster.expand": "Show {count} more tasks",
  "priorities.cluster.collapse": "Collapse task group",
  "priorities.unplace.hint": "Drop here to take it off the board — importance, urgency and priority are cleared.",
  "priorities.queue.title": "Suggested order",
  "priorities.queue.empty": "Nothing to decide in this range.",
  "priorities.band.now": "Do now",
  "priorities.band.next": "Up next",
  "priorities.band.later": "Later",
  "priorities.band.unranked": "Unranked",
  "priorities.focusPick": "Focus pick",
  "priorities.strip.title": "Next 14 days",
  "priorities.strip.clear": "Show whole range",
  "priorities.strip.dropSchedule": "Drop to schedule on the highlighted day.",
  "priorities.strip.recurringDrop": "This task repeats — reschedule it on the calendar.",
  "priorities.strip.capacity": "Daily capacity",
  "priorities.strip.capacityOff": "No limit",
  "priorities.ladder.title": "Schedule on {day}",
  "priorities.ladder.keep": "Keep time",
  "priorities.ladder.allDay": "All day",
  "priorities.ladder.morning": "Morning",
  "priorities.ladder.afternoon": "Afternoon",
  "priorities.ladder.evening": "Evening",
  "priorities.ladder.exact": "Exact time…",
  "priorities.ladder.commit": "Schedule",
  "priorities.ladder.failed": "Could not schedule the task.",
  "priorities.update.failed": "Could not update the item.",
  "priorities.queue.dropBand": "Drop to file under “{band}”.",
  "priorities.queue.dropHere": "Drop a task here.",
  "priorities.stale.badge": "{weeks} wk",
  "priorities.stale.hint": "Untouched for {weeks} weeks — keep, snooze, or delete?",
  "priorities.plan.button": "Plan this day",
  "priorities.plan.title": "Plan {day}",
  "priorities.plan.intro": "Suggested times in the free gaps around what's already scheduled. Untick anything you don't want moved.",
  "priorities.plan.empty": "Nothing to plan — no open task needs a time on this day.",
  "priorities.plan.unfit": "No room left for:",
  "priorities.plan.apply": "Schedule {count}",
  "priorities.plan.applied": "Scheduled {count} tasks",
  "priorities.plan.failed": "Could not finish scheduling. Some tasks may already have moved.",
  "priorities.dueNone": "No date",
  "priorities.overdueToday": "Earlier today",
  "priorities.overdueBy": "{days} d overdue",
  "priorities.hide.action": "Hide from the matrix",
  "priorities.hide.toast": "Hidden from the matrix.",
  "priorities.hidden.title": "Hidden from the matrix ({count})",
  "priorities.hidden.hint": "These still occupy time on the calendar and in the day load — they just aren't decisions. Hiding always applies to the whole series.",
  "priorities.hidden.show": "Show",
  "priorities.enable.title": "Turn on the priority matrix",
  "priorities.enable.body": "Rate tasks by importance and urgency to get a suggested order. Enable the matrix to place tasks on the board — the list and the day strip work either way.",
  "priorities.enable.forTasks": "Enable for tasks",
  "priorities.enable.settings": "Open settings",
  "sidebar.filterPriority": "Filter by {label} priority",
  "sidebar.filteringPriority": "Filtering by {label} priority -- click to remove",
  "sidebar.urgency": "Urgency",
  "sidebar.filterUrgency": "Filter by {label} urgency",
  "sidebar.filteringUrgency": "Filtering by {label} urgency -- click to remove",
  "sidebar.urgency.none": "Unplaced",
  "sidebar.calendarState": "{name} calendar -- {state}",
  "sidebar.hiddenClickShow": "hidden, click to show",
  "sidebar.shownClickHide": "shown, click to hide",
  "sidebar.hiddenTitle": "Hidden -- click to show",
  "sidebar.shownTitle": "Shown -- click to hide",
  "sidebar.dragCalendar": "Drag to reorder calendar",
  "sidebar.filteringLabel": "Filtering by this label -- click to remove",
  "sidebar.filterByLabel": "Click to filter by this label",
  "sidebar.dragLabel": "Drag to reorder label",
  "sidebar.filteringTopic": "Filtering by this topic -- click to remove",
  "sidebar.filterByTopic": "Click to filter by this topic",
  "sidebar.dragTopic": "Drag to reorder topic",

  "aiQuickAdd.title": "Add with Claude",
  "aiQuickAdd.placeholder": "e.g. remind me to call mom every Tuesday at 6pm",
  "aiQuickAdd.missingKey": "Set your Anthropic API key in Settings first.",
  "aiQuickAdd.invalidResponse": "Claude returned reminder details that could not be understood. Try rephrasing the reminder.",
  "aiQuickAdd.thinking": "Thinking…",

  "today.title": "Today",
  "today.overdue": "Overdue",
  "today.snoozed": "Snoozed",
  "today.empty": "Nothing scheduled today.",
  "today.emptyDay": "Nothing scheduled.",
  "today.wakes": "Wakes {time}",
  "today.wakeNow": "Wake now",
  "today.relative.today": "Today",
  "today.relative.tomorrow": "Tomorrow",
  "today.greeting.morning": "Good morning",
  "today.greeting.afternoon": "Good afternoon",
  "today.greeting.evening": "Good evening",
  "today.chip.tasks": "Open",
  "today.chip.events": "Events",
  "today.chip.done": "Done",
  "today.upNext": "Up next",
  "today.happeningNow": "Happening now",
  "today.startsIn": "in {duration}",
  "today.until": "until {time}",
  "today.allClear": "All clear — every task for today is done!",
  "today.dayDone": "All tasks done",
  "today.openCount": "{count} open",
  "today.scheduledCount": "{count} scheduled",
  "today.hideDone": "Hide completed",
  "today.showDone": "Show completed",
  "today.showAllOverdue": "Show all {count}",
  "today.showLess": "Show less",
  "today.moveToToday": "Move to today",
  "today.movedToToday": "Moved to today",
  "today.moveFailed": "Could not move the task to today.",
  "today.rescheduled": "Task rescheduled",
  "today.rescheduleFailed": "Could not reschedule the task.",
  "today.snoozeFailed": "Could not snooze the overdue tasks.",
  "today.overdueBy": "{days} d overdue",
  "today.overdueToday": "Earlier today",
  "today.now": "Now",
  "today.weekOverview": "Agenda overview",
  "today.dayFree": "Free",
  "today.chip.scheduled": "Scheduled",
  "today.tomorrowFree": "Tomorrow is free.",
  "today.tomorrowPreview": "Tomorrow: {count} scheduled · first at {time}",
  "today.tomorrowPreviewNoTime": "Tomorrow: {count} scheduled",
  "today.deadline": "Deadline: {date}",
  "today.subtasks": "{done} of {total} subtasks done",
  "today.rescheduleChip": "Change time",
  "today.openInCalendar": "Open in calendar",
  "today.planDay": "Plan my day",
  "today.chip.inbox": "Inbox",
  "today.sortAge": "Oldest first",
  "today.sortPriority": "By priority",
  "today.bulkMoveAll": "All to today",
  "today.bulkSnoozeAll": "Snooze all",
  "today.bulkMoved": "Moved {count} to today",
  "today.bulkMoveFailed": "Could not move all overdue tasks to today.",
  "today.bulkSnoozed": "Snoozed {count} until tomorrow",
  "today.collapseSection": "Collapse section",
  "today.expandSection": "Expand section",
  "today.moveModule": "Drag to rearrange",
  "today.moduleWidth": "Module width",

  "agenda.allDay": "All day",
  "agenda.markDone": "Mark done",
  "agenda.markNotDone": "Mark not done",
  "agenda.snoozeTitle": "Snooze",
  "agenda.showOnCalendar": "Show on calendar",
  "agenda.clickToEdit": "Click to edit",
  "agenda.untitled": "(untitled)",
  "agenda.recurring": "recurring",

  "tasks.title": "All tasks",
  "tasks.empty": "No tasks yet.",
  "tasks.due": "Due",
  "tasks.deadline": "Deadline",
  "tasks.duration": "Duration",
  "tasks.priority": "Priority",
  "tasks.urgency": "Urgency",
  "tasks.titleColumn": "Title",
  "tasks.noDate": "No date",
  "tasks.deadlineAt": "Deadline {time}",
  "tasks.tapToEdit": "Tap to edit",
  "tasks.doubleClickToEdit": "Double-click to edit",
  "tasks.markOpen": "Mark task open",
  "tasks.markDone": "Mark task done",

  "search.placeholder": "Search tasks by title, notes, or location...",
  "search.hint": "Type to search across all your tasks.",
  "search.noMatch": "No tasks match \"{query}\".",
  "search.view": "View",
  // Shared by every capped list render (see lib/paged-list.tsx).
  "list.showing": "Showing {shown} of {total}",
  "list.showMore": "Show more",

  "inbox.description": "Undated tasks. Drag any of them onto a calendar view to schedule it as a time block.",
  "inbox.empty": "No undated tasks.",
  "inbox.panelEmpty": "No undated items. Drag a block here to un-schedule it, or add one.",
  "inbox.longPressTitle": "Long-press to drag onto the calendar",
  "inbox.dragTitle": "Drag onto the calendar to schedule",
  "inbox.sort": "Sort",
  "inbox.sort.default": "Default",
  "inbox.sort.priority": "Priority",
  "inbox.sort.urgency": "Urgency",
  "inbox.sort.title": "Title",
  "inbox.toggle": "Toggle inbox",

  "datePicker.selectDate": "Select date",
  "datePicker.tomorrow": "Tomorrow",
  "datePicker.nextWeek": "Next week",

  "snooze.until": "Snooze until",
  "snooze.preset.evening": "This evening",
  "snooze.preset.tomorrow": "Tomorrow morning",
  "snooze.preset.weekend": "This weekend",
  "snooze.preset.week": "Next week",
  "snooze.custom": "Custom...",
  "snooze.commit": "Snooze",

  "recurring.deleteTitle": "Delete recurring {noun}",
  "recurring.editTitle": "Edit recurring {noun}",
  "recurring.move": "Move \"{title}\" from {from} to {to}?",
  "recurring.moveAllDay": "Move \"{title}\" from {from} to all day on {date}?",
  "recurring.resize": "Set duration of \"{title}\" to {duration}?",
  "recurring.delete": "Delete \"{title}\"?",
  "recurring.edit": "Edit \"{title}\"?",
  "recurring.rename": "Rename \"{title}\" to \"{nextTitle}\"?",
  "recurring.partOfSeries": "This {noun} is part of a recurring series.",
  "recurring.onlyThis": "Only this {noun}",
  "recurring.thisAndFollowing": "This and following {noun}s",
  "recurring.wholeSeries": "Whole series",
  "recurring.all": "All {noun}s",

  "taskForm.task": "Task",
  "taskForm.event": "Event",
  "taskForm.starts": "Starts",
  "taskForm.ends": "Ends",
  "taskForm.deadline": "Deadline",
  "taskForm.duration": "Duration",
  "taskForm.allDay": "All-day",
  "taskForm.allDayLine1": "All",
  "taskForm.allDayLine2": "Day",
  "taskForm.reminder": "Reminder",
  "taskForm.repeating": "Repeating",
  "taskForm.noTopic": "No topic",
  "taskForm.untitledTask": "Untitled task...",
  "taskForm.untitledEvent": "Untitled event...",
  "taskForm.notesPlaceholder": "Notes (Markdown supported)",
  "taskForm.addNotes": "Add notes",
  "taskForm.markdownPreview": "Markdown preview",
  "taskForm.advancedOptions": "Advanced options",
  "taskForm.markComplete": "Mark complete",
  "taskForm.markUncomplete": "Mark uncomplete",
  "taskForm.saving": "Saving...",
  "taskForm.createTask": "Create task",
  "taskForm.createEvent": "Create event",
  "taskForm.helper": "Press {mod} + Enter to save · esc to cancel",
  "taskForm.switchToAllDay": "Switch to all-day",
  "taskForm.durationOrAllDay": "Duration or all-day",
  "taskForm.labels": "Labels",
  "taskForm.noLabels": "No labels yet -- add some in Settings.",
  "taskForm.timeZone": "Time zone",
  "taskForm.deviceLocal": "Device local (floating)",
  "taskForm.remindBeforeDue": "Remind (before due)",
  "taskForm.pickDate": "Pick date",
  "taskForm.pickTime": "Pick time",
  "taskForm.selectTime": "Select time",
  "taskForm.clearTime": "Clear time",
  "taskForm.clearDate": "Clear date",
  "taskForm.clearDateTitle": "Clear date (keep in Inbox)",
  "taskForm.startDateTime": "Start date and time",
  "taskForm.endDateTime": "End date and time",
  "taskForm.setDate": "Set date",
  "taskForm.setTime": "Set time",
  "taskForm.endDate": "End date",
  "taskForm.end": "End",
  "taskForm.addDeadline": "Add deadline",
  "taskForm.clearDeadline": "Clear deadline",
  "taskForm.editTask": "Edit task",
  "taskForm.newTask": "New task",
  "taskForm.discardTitle": "Discard this new item?",
  "taskForm.discardCopy": "You have unsaved details in this form. Keep editing to preserve them, or discard to close without saving.",
  "taskForm.keepEditing": "Keep editing",
  "taskForm.discard": "Discard",
  "taskForm.discardChangesTitle": "Discard changes?",
  "taskForm.discardMobileCopy": "You have unsaved {kind}. Keep editing to preserve them.",
  "taskForm.changes": "changes",
  "taskForm.details": "details",
  "taskForm.reopen": "Reopen",
  "taskForm.noPriority": "No priority",
  "taskForm.subtasks": "Subtasks",
  "taskForm.subtask": "Subtask...",
  "taskForm.scheduled": "scheduled",
  "taskForm.removeSubtask": "Remove subtask",
  "taskForm.addSubtask": "Add subtask",
  "taskForm.location": "Location",
  "taskForm.addLocation": "Add location",
  "taskForm.noMatches": "No matches",
  "taskForm.clear": "Clear",
  "taskForm.deviceDefault": "Device default",
  "taskForm.chooseDifferentTopic": "Choose a different topic",
  "taskForm.topic": "Topic",
  "taskForm.collapseTopics": "Collapse topics",
  "taskForm.shorter": "Shorter",
  "taskForm.longer": "Longer",

  "color.custom": "Custom color",
  "color.noColor": "No color",
  "color.red": "Red",
  "color.orange": "Orange",
  "color.yellow": "Yellow",
  "color.green": "Green",
  "color.teal": "Teal",
  "color.blue": "Blue",
  "color.purple": "Purple",
  "color.pink": "Pink",
  "color.lime": "Lime",
  "color.cyan": "Cyan",
  "color.indigo": "Indigo",
  "color.slate": "Slate",

  "reminder.remindMe": "Remind me",
  "reminder.selectAny": "select any",
  "reminder.atTime": "At time",
  "reminder.minBefore": "{count} min before",
  "reminder.hourBefore": "{count} hour before",
  "reminder.hoursBefore": "{count} hours before",
  "reminder.dayBefore": "Day before",
  "reminder.daysBefore": "{count} days before",
  "reminder.custom": "Custom",
  "reminder.beforeStart": "before start",
  "reminder.remove": "Remove reminder",
  "reminder.minutes": "minutes",
  "reminder.hours": "hours",
  "reminder.days": "days",

  "repeat.frequency": "Frequency",
  "repeat.daily": "Daily",
  "repeat.weekly": "Weekly",
  "repeat.monthly": "Monthly",
  "repeat.yearly": "Yearly",
  "repeat.onDays": "On days",
  "repeat.every": "Every",
  "repeat.day": "day",
  "repeat.days": "days",
  "repeat.week": "week",
  "repeat.weeks": "weeks",
  "repeat.month": "month",
  "repeat.months": "months",
  "repeat.year": "year",
  "repeat.years": "years",
  "repeat.endDate": "End date",
  "repeat.seriesEndDate": "Series end date",
  "repeat.endsOn": "Ends {date}",
  "repeat.noEndDate": "No end date",
  "repeat.noEnd": "No end -- repeats forever",

  "sync.account": "Account",
  "sync.emailUnverified": "Your email isn't verified yet. Check your inbox for the link.",
  "sync.resend": "Resend",
  "sync.signedInAs": "Signed in as {email}",
  "sync.logOut": "Log out",
  "sync.deleteAccountDescription": "Permanently delete your account and all synced data from the server.",
  "sync.deleteAccount": "Delete account",
  "sync.signupDescription": "Create an account to sync this device with your others.",
  "sync.loginDescription": "Sign in to sync your tasks, events & lists across devices.",
  "sync.passwordSignup": "Password (min 8 characters)",
  "sync.password": "Password",
  "sync.createAccount": "Create account",
  "sync.signIn": "Sign in",
  "sync.haveAccount": "Have an account? Sign in",
  "sync.newHere": "New here? Create an account",
  "sync.forgotPassword": "Forgot password?",
  "sync.enterEmailFirst": "Enter your email above first.",
  "sync.deleteAccountBody": "This permanently deletes your account{email} and all data synced to the server. This cannot be undone. Tasks stored on this device are not removed -- use \"Delete all data\" for that.",
  "sync.deleteMyAccount": "Delete my account",
  "sync.multiDevice": "Multi-device sync",
  "sync.multiDevice.description": "Your tasks, events & lists stay in step across every device you sign in on. Changes merge field-by-field and deletes propagate. Sign in with the Account card above to turn on syncing.",
  "sync.syncing": "Syncing...",
  "sync.syncNow": "Sync now",
  "sync.signInToSync": "Sign in to sync",
  "sync.setServer": "Set a sync server under Advanced",
  "sync.lastSynced": "Last synced {time}",
  "sync.enabled": "Syncing enabled",
  "sync.advancedServer": "Advanced: sync server",
  "sync.serverDescription": "Defaults to the hosted server. Change this only if you run your own.",
  "sync.saveServer": "Save server URL",
  "sync.googleCalendar": "Google Calendar",
  "sync.googleNotConfigured": "Google Calendar sync isn't enabled in this build (no client id configured).",
  "sync.googleConnectDescription": "Connect your Google account to sync events both ways while the app is open.",
  "sync.connecting": "Connecting...",
  "sync.connectGoogle": "Connect Google Calendar",
  "sync.continueWithGoogle": "Continue with Google",
  "sync.signedInWithGoogle": "Signed in with Google.",
  "sync.googleSeparateNote": "Connecting Google Calendar is separate from signing in — it only adds two-way calendar sync (web needs an account/sync server).",
  "onboarding.title": "Welcome to YourCalendar",
  "onboarding.subtitle": "Choose how to get started — you can change this later in Settings.",
  "onboarding.accountNote": "Sync your tasks, events & lists across devices, with online backup.",
  "onboarding.createAccount": "Create an account",
  "onboarding.signInInstead": "Already have an account? Sign in",
  "onboarding.continueWithGoogle": "Continue with Google",
  "onboarding.continueWithout": "Continue without an account",
  "onboarding.withoutNote": "No online backup and no sync between devices — everything stays on this device.",
  "onboarding.back": "Back",
  "onboarding.intro": "Choose the essentials now. Everything here stays editable in Settings.",
  "onboarding.skip": "Skip setup",
  "onboarding.stepAccount": "Account",
  "onboarding.stepNotifications": "Notifications",
  "onboarding.stepImport": "Import & sync",
  "onboarding.stepTheme": "Theme",
  "onboarding.stepOrganization": "Organization",
  "onboarding.stepTime": "Locale & time",
  "onboarding.notifTitle": "Reminder notifications",
  "onboarding.notifBody": "Send a test notification now so reminders can ask for the permission they need.",
  "onboarding.notifTest": "Send test notification",
  "onboarding.notifTesting": "Testing...",
  "onboarding.notifTested": "Test sent.",
  "onboarding.notifScheduled": "Notification scheduled with the OS.",
  "onboarding.notifSent": "Test notification sent.",
  "onboarding.importIcsTitle": "Import .ics calendar",
  "onboarding.importIcsBody": "Bring in events and recurring entries from an iCalendar file.",
  "onboarding.importing": "Importing...",
  "onboarding.googleBody": "Connect Google Calendar and choose calendars to bind after setup.",
  "onboarding.connected": "Connected",
  "onboarding.topicsBody": "Your topic groups, exactly as in Settings: drag the grip to reorder, drag a row right to nest it as a sub-topic (left to promote it), click the emoji slot for an icon.",
  "onboarding.labelsBody": "Edit cross-cutting tags for priority, status, or context.",
  "onboarding.themePreviewTitle": "Live preview",
  "onboarding.themePreviewBody": "A week of example tasks rendered with the theme you pick above — choose a preset and watch it re-color instantly.",
  "onboarding.webAvailableTitle": "Also available on the web",
  "onboarding.webAvailableBody": "Use YourCalendar from any browser at yourcalendar.app.",
  "onboarding.openWebApp": "Open yourcalendar.app",
  "onboarding.appAvailableTitle": "Also available as an Android app",
  "onboarding.appAvailableBody": "Install YourCalendar from Google Play for the full mobile app.",
  "onboarding.openPlayStore": "Get it on Google Play",
  "sync.connectedAs": "Connected as {email}",
  "sync.googleAccount": "Google account",
  "sync.disconnect": "Disconnect",
  "sync.loadingCalendars": "Loading your calendars...",
  "sync.primary": "(primary)",
  "sync.twoWay": "Two-way",
  "sync.googleToApp": "Google -> app",
  "sync.appToGoogle": "App -> Google",
  "sync.paused": "Paused",
  "sync.addToApp": "Add to app",

  "trash.description": "Deleted tasks and events. Restore them, or remove them for good.",
  "trash.emptyTrash": "Empty trash",
  "trash.empty": "Trash is empty. Deleted tasks and events show up here.",
  "trash.deletedAt": "deleted {time}",
  "trash.deletePermanently": "Delete permanently",
  "trash.emptyConfirm": "This permanently deletes all {count} item(s) in the trash. This cannot be undone.",
  "trash.deleteConfirm": "This permanently deletes \"{title}\". This cannot be undone.",

  "rollup.complete": "Complete",
  "rollup.reopen": "Reopen",
  "rollup.completed": "completed",
  "rollup.reopened": "reopened",
  "rollup.title": "{verb} with subtasks?",
  "rollup.body": "\"{title}\" has related tasks. Should they be {what} too?",
  "rollup.cascadeDown": "{verb} this and all its subtasks",
  "rollup.cascadeUp": "{verb} this and its parent task",
  "rollup.onlyThis": "Only this {noun}",

  "resetPassword.title": "Reset your password",
  "resetPassword.missingToken": "This link is missing its token. Request a new reset email from the app.",
  "resetPassword.done": "Your password has been updated. Sign in with your new password.",
  "resetPassword.openApp": "Open YourCalendar",
  "resetPassword.newPassword": "New password (min 8 characters)",
  "resetPassword.confirmPassword": "Confirm new password",
  "resetPassword.mismatch": "Passwords don't match.",
  "resetPassword.submit": "Set new password",

  "demos.replay": "Replay",
  "demos.pause": "Pause",
  "demos.resume": "Resume",
  "demos.loading": "Loading demo…",
  "demos.failed": "The demo couldn't be shown here. Everything it demonstrates works normally in the app.",
  "demos.title.basicsReschedule": "Drag to reschedule",
  "demos.cap.reschedule.intro": "To reschedule a task, simply drag its block…",
  "demos.cap.reschedule.drag": "…to a new day and time.",
  "demos.cap.reschedule.moved": "Dropped — the task now lives in the new slot.",
  "demos.cap.reschedule.complete": "A tap on its checkbox marks it done.",
  "demos.fx.standup": "Team standup",
  "demos.fx.writeReport": "Write report",
  "demos.fx.designReview": "Design review",
  "demos.fx.lunch": "Lunch with Alex",
  "demos.fx.gym": "Gym",
  "demos.nestDemoTitle": "Group work into subtasks",
  "demos.nestDemoBody": "Any task can live inside another: drop it onto a block's title to nest it.",
  "demos.title.basicsNest": "Drag onto a block to nest",
  "demos.cap.nest.intro": "A task can become a subtask of another block…",
  "demos.cap.nest.dwell": "…hold it over the target's title for a moment…",
  "demos.cap.nest.nested": "…and it nests inside its parent, right in the calendar.",
  "demos.fx.planTrip": "Plan summer trip",
  "demos.fx.bookHotel": "Book hotel",
  "demos.title.matrixScope": "Flags vs. priority matrix",
  "demos.cap.matrixScope.flags": "Matrix off: tasks AND events carry a classic priority flag — three fixed colors.",
  "demos.cap.matrixScope.matrixOn": "Switched to “Only for tasks”: every task moves onto the 3×3 matrix — priority is now importance × urgency.",
  "demos.cap.matrixScope.diagonal": "Every matrix position gets its own continuous color along the green→red diagonal.",
  "demos.cap.matrixScope.eventsKeepFlags": "The lunch EVENT didn't change — in “Only for tasks” mode, events keep their classic flag.",
  "demos.cap.matrixScope.eventsToo": "Switched to “Tasks & events”: now events use the matrix as well.",
  "demos.cap.matrixScope.eventPlaced": "The event gets its own spot — urgent but not important, so its color shifts toward orange.",
  "demos.fx.errands": "Run errands",
  "demos.fx.prepSlides": "Prepare slides",
  "demos.fx.quarterlyReport": "Quarterly report",
  "demos.title.deadlineUrgency": "Urgency follows the deadline",
  "demos.cap.deadline.intro": "With auto urgency, a task's urgency measures how close its deadline is to today.",
  "demos.cap.deadline.today": "This first column is today — the reference day distance is measured from.",
  "demos.cap.deadline.far": "This task is due in 4 days — urgency: low.",
  "demos.cap.deadline.drag": "Drag it closer to today…",
  "demos.cap.deadline.medium": "Now due in 2 days — urgency rises to medium.",
  "demos.cap.deadline.high": "Due tomorrow — urgency jumps to high.",
  "demos.cap.deadline.tracked": "Matrix column and block color follow the deadline automatically — every day, on their own.",
  "demos.fx.taxes": "File taxes",
  "demos.title.subtaskRollup": "Subtask completion roll-up",
  "demos.title.todayTour": "The Today screen",
  "demos.cap.today.intro": "Today gathers your whole day — schedule, progress and anything overdue.",
  "demos.cap.today.complete": "Tick tasks off right from the list.",
  "demos.cap.today.overdue": "Missed items collect in Overdue.",
  "demos.cap.today.moveToday": "One tap moves them onto today's plan.",
  "demos.cap.today.snooze": "Not today? Snooze it — it waits quietly until tomorrow.",
  "demos.fx.reviewBudget": "Review budget",
  "demos.fx.teamLunch": "Team lunch",
  "demos.fx.draftReport": "Draft report",
  "demos.fx.callBank": "Call the bank",
  "demos.cap.rollup.twoWay": "Two-way roll-up: check off the subtasks one by one…",
  "demos.cap.rollup.childrenDone": "…all subtasks done — the parent completes itself.",
  "demos.cap.rollup.parentMode": "Parent → children mode: now complete the parent instead…",
  "demos.cap.rollup.parentDone": "…and every subtask follows.",
  "demos.fx.renovation": "Renovate hallway",
  "demos.fx.paint": "Buy paint",
  "demos.fx.orderFloor": "Order flooring",
  "demos.title.themePreview": "Live calendar preview",
  "demos.topic.work": "Work",
  "demos.topic.family": "Family",
  "demos.topic.health": "Health",
  "demos.topic.home": "Home",
  "demos.fx.dinner": "Dinner with friends",
  "demos.fx.hike": "Morning hike",
  "demos.showMe": "Show me",
  "demos.hideDemo": "Hide demo",
  "demos.compare.flagsTitle": "Priority flags",
  "demos.compare.flagsNote": "Three fixed levels — every entry is exactly low, medium or high.",
  "demos.compare.matrixTitle": "3×3 priority matrix",
  "demos.compare.matrixNote": "Priority is the diagonal of importance × urgency — drag the dot and watch the color shift continuously.",
  "demos.compare.usedFor": "Used for:",
  "demos.compare.notUsed": "Not used in this mode",
  "demos.compare.chip.tasks": "Tasks",
  "demos.compare.chip.events": "Events",
  "demos.compare.summary.off": "Current mode: tasks and events both use classic priority flags — the matrix is off.",
  "demos.compare.summary.tasks": "Current mode: tasks use the 3×3 matrix; events keep classic priority flags.",
  "demos.compare.summary.tasksEvents": "Current mode: tasks and events both use the 3×3 matrix.",
  "demos.rail.matrixOff": "Matrix off — classic flags",
  "widget.nextUpEmpty": "All clear",
  "widget.focusTitle": "Top priorities",
  "widget.focusEmpty": "No open priorities",
  "widget.overdue": "{count} overdue",

  // Calendar block context menu + block affordances.
  "contextMenu.markComplete": "Mark complete",
  "contextMenu.markUncomplete": "Mark uncomplete",
  "contextMenu.wakeNow": "Wake now",
  "contextMenu.snooze": "Snooze...",
  "contextMenu.createTaskHere": "Create task here",
  "contextMenu.duplicateTask": "Duplicate task",
  "contextMenu.duplicateEvent": "Duplicate event",
  "contextMenu.moveToParent": "Move to parent task",
  "calendar.dragToMove": "Drag to change start time",
  "calendar.dragToResize": "Drag to resize",
  "calendar.snoozedLabel": "snoozed",
  "calendar.createTask": "Create task",
  "calendar.insert": "Insert",
  "calendar.showLess": "Show less",
  "calendar.subtaskRowHint": "Click to edit - drag to reschedule or reorder",
  "sidebar.resize": "Resize sidebar",
  "common.decrease": "Decrease",
  "common.increase": "Increase",
  "theme.previewMuted": "Aa - muted text - secondary surfaces",
  "sync.googleEncoding.tasks": "Tasks sync as events too: a task shows as \"⬜ Title\" (done → \"✅\"). Swap ⬜/✅ to complete it, or type \"[ ]\" or \"Task:\" in front of a title to turn an event into a task.",
  "sync.googleEncoding.subtasks": "Subtasks appear in the description as ☐/☑. To tick one, replace its box with \"[x]\"; add a new \"[ ] subtask\" line to create one.",
  "ai.dailyDigest": "Daily digest",
  "ai.dailyDigest.generate": "Generate",
  "ai.dailyDigest.generating": "Generating…",
  "ai.dailyDigest.refresh": "Refresh",
  "ai.dailyDigest.empty": "Click Generate for a one-paragraph overview of your day.",
  "color.pickCustom": "Pick custom color",
  "notification.fallbackTitle": "Reminder",
  "notification.due": "Due {time}",
  "notification.bundleTitle": "{count} reminders",
  "notification.channelName": "Reminders",
  "notification.channelDescription": "Task and event reminders",
  "notification.snooze10": "Snooze 10 min",
  "notification.snooze60": "Snooze 1 hour",
  "notification.testTitle": "Test notification",
  "notification.testBackground": "Scheduled test fired - background delivery works.",
  "notification.testImmediate": "Immediate test - notifications are working.",
  "notification.notPermitted": "Notifications aren't permitted. Enable them in Android Settings > Apps > YourCalendar > Notifications.",

  // Imperative toasts fired from stores / async flows (see lib/tr.ts).
  "common.conflict": "conflict",
  "common.conflicts": "conflicts",
  "toast.genericError": "Something went wrong. If this keeps happening, try reloading.",
  "toast.file.invalidJson": "That file isn't valid JSON.",
  "toast.server.waking": "Waking up the server -- the first connection after a while can take up to a minute...",
  "toast.backup.saved": "Backup saved.",
  "toast.backup.failed": "Backup failed: {error}",
  "toast.backup.notValid": "Not a valid backup file: {error}",
  "toast.backup.restored": "Backup restored.",
  "toast.backup.merged": "Backup merged in.",
  "toast.backup.restoreFailed": "Restore failed: {error}",
  "toast.data.deleted": "All data deleted.",
  "toast.data.deleteFailed": "Delete failed: {error}",
  "toast.settings.noneInFile": "No settings found in that file.",
  "toast.settings.imported": "Settings imported.",
  "toast.ics.nothingToExport": "Nothing to export for that selection.",
  "toast.ics.exported": "Exported {count} {noun} (.ics).",
  "toast.ics.exportFailed": "Export failed: {error}",
  "toast.ics.noneImportable": "No importable events found in the chosen files.",
  "toast.ics.imported": "Imported {count} {noun}.",
  "toast.ics.importedSkipped": "Imported {count} {noun} ({skipped} already present).",
  "toast.ics.importFailed": "Import failed: {error}",
  "toast.sync.setUrlFirst": "Set a sync server URL first.",
  "toast.sync.signInFirst": "Sign in to sync.",
  "toast.sync.done": "Synced (down {pulled}, up {pushed}).",
  "toast.sync.doneWithConflicts": "Synced (down {pulled}, up {pushed}); {count} {noun} resolved.",
  "toast.sync.failed": "Sync failed: {error}",
  "toast.trash.taskRestored": "Task restored",
  "toast.trash.eventRestored": "Event restored",
  "toast.undo.done": "Undone.",
  "toast.undo.failed": "Undo failed: {error}",
  // Confirmation line on every undoable task mutation (see stores/undo.ts).
  "toast.task.added": "Task added",
  "toast.task.created": "Task created",
  "toast.task.updated": "Task updated",
  "toast.task.completed": "Task completed",
  "toast.task.reopened": "Task reopened",
  "toast.task.deleted": "Task deleted",
  "toast.task.woken": "Task woken",
  "toast.task.duplicated": "Task duplicated",
  "toast.task.eventDuplicated": "Event duplicated",
  "toast.task.snoozedUntil": "Snoozed until {when}",
  "toast.task.subtasksReordered": "Subtasks reordered",
  "toast.task.occurrenceUpdated": "Occurrence updated",
  "toast.task.occurrenceRescheduled": "Occurrence rescheduled",
  "toast.task.occurrenceResized": "Occurrence resized",
  "toast.task.occurrenceCompleted": "Occurrence completed",
  "toast.task.occurrenceReopened": "Occurrence reopened",
  "toast.task.seriesRescheduled": "Series rescheduled",
  "toast.task.seriesResized": "Series resized",
  "toast.task.seriesUpdated": "Series updated",
  "toast.task.seriesEndChanged": "Series end changed",
  "toast.task.deletedFromSeries": "Deleted",
  "toast.auth.googleNotConfigured": "Google sign-in isn't configured in this build.",
  "toast.auth.googleSignedIn": "Signed in with Google.",
  "toast.auth.accountCreated": "Account created.",
  "toast.auth.signedIn": "Signed in.",
  "toast.auth.accountDeleted": "Account deleted.",
  "toast.auth.resetLinkSent": "If that email has an account, a reset link is on its way.",
  "toast.auth.passwordUpdated": "Password updated. Sign in with your new password.",
  "toast.auth.verificationSent": "Verification email sent.",
  "toast.google.notConfigured": "Google Calendar isn't configured in this build.",
  "toast.google.setUrlFirst": "Set a sync server URL first (Settings -> Sync) -- web Google sync exchanges tokens through it.",
  "toast.google.noRefreshToken": "Google didn't return a refresh token. Remove the app's access at myaccount.google.com and reconnect.",
  "toast.google.connectedAs": "Connected {email}.",
  "toast.google.connected": "Connected Google Calendar.",
  "toast.google.syncFailed": "Google Calendar sync failed: {error}",
  "toast.google.syncedWithFailures": "Synced -- {count} {noun} couldn't be sent (skipped).",
  "toast.google.synced": "Google Calendar synced.",
  "toast.google.conflictsResolved": "{count} {noun} resolved (Google's version kept).",
} as const;

export type I18nKey = keyof typeof EN;

const DE: Record<I18nKey, string> = {
  "common.account": "Konto",
  "common.add": "Hinzufügen",
  "common.advanced": "Erweitert",
  "common.allDay": "Ganztägig",
  "common.allTasks": "Alle Aufgaben",
  "common.back": "Zurück",
  "common.backToSettings": "Zurück zu den Einstellungen",
  "common.backup": "Backup",
  "common.build": "Build",
  "common.calendar": "Kalender",
  "common.calendars": "Kalender",
  "common.cancel": "Abbrechen",
  "common.claude": "Claude",
  "common.clear": "Leeren",
  "common.clearAll": "Alle abwählen",
  "common.closeDialog": "Dialog schließen",
  "common.color": "Farbe",
  "common.complete": "Erledigen",
  "common.create": "Erstellen",
  "common.day": "Tag",
  "common.days": "Tage",
  "common.delete": "Löschen",
  "common.default": "Standard",
  "common.developer": "Entwickler",
  "common.done": "Fertig",
  "common.edit": "Bearbeiten",
  "common.event": "Termin",
  "common.events": "Termine",
  "common.export": "Exportieren",
  "common.feedback": "Feedback",
  "common.filters": "Filter",
  "common.hide": "Ausblenden",
  "common.hidden": "Ausgeblendet",
  "common.import": "Importieren",
  "common.inbox": "Eingang",
  "common.language": "Sprache",
  "common.lists": "Listen",
  "common.labels": "Labels",
  "common.loading": "Lädt...",
  "common.month": "Monat",
  "common.navigation": "Navigation",
  "common.new": "Neu",
  "common.newTask": "Neue Aufgabe",
  "common.next": "Weiter",
  "common.noDate": "Kein Datum",
  "common.none": "keine",
  "common.notes": "Notizen",
  "common.off": "Aus",
  "common.on": "Ein",
  "common.previous": "Zurück",
  "common.moreInfo": "Weitere Informationen",
  "common.remove": "Entfernen",
  "common.reset": "Zurücksetzen",
  "common.restore": "Wiederherstellen",
  "common.save": "Speichern",
  "common.search": "Suche",
  "common.selectAll": "Alle auswählen",
  "common.settings": "Einstellungen",
  "common.show": "Anzeigen",
  "common.shown": "Angezeigt",
  "common.sync": "Sync",
  "common.task": "Aufgabe",
  "common.tasks": "Aufgaben",
  "common.today": "Heute",
  "common.topics": "Themen",
  "common.trash": "Papierkorb",
  "common.version": "Version",
  "common.week": "Woche",

  "nav.main": "Hauptnavigation",
  "nav.title": "Kalender",

  "settings.category.general": "Allgemein",
  "settings.category.general.blurb": "Sprache, Zeit, Region, Start",
  "settings.category.appearance": "Darstellung",
  "settings.category.appearance.blurb": "Design, Text, Blockfarbe",
  "settings.category.theme": "Oberflächenfarben",
  "settings.category.theme.blurb": "Farben, Paletten, Vorlagen",
  "theme.title": "Design",
  "theme.preset": "Design-Vorlage",
  "theme.preset.description": "Wähle ein Ausgangsdesign. Passe unten alles an; mit Zurücksetzen stellst du eine Änderung wieder her.",
  "theme.custom": "Eigenes",
  "theme.source": "Quellfarbe",
  "theme.source.description": "Das gesamte Farbschema wird aus dieser Farbe erzeugt.",
  "theme.sourceLight": "Quelle (hell)",
  "theme.sourceDark": "Quelle (dunkel, optional)",
  "theme.sourceRevert": "Auf Voreinstellungsfarbe zurücksetzen",
  "theme.variant": "Stil",
  "theme.revertAll": "Auf Vorlage zurücksetzen",
  "theme.syncTitle": "Design geräteübergreifend synchronisieren",
  "theme.syncDescription": "Teile dieses Design mit deinen anderen angemeldeten Geräten. Unterscheiden sich zwei Geräte, wirst du gefragt, welches gelten soll.",
  "theme.roles": "Erzeugte UI-Farben",
  "theme.roles.description": "Jede Oberflächenfarbe wird aus der Quelle erzeugt. Überschreibe sie beliebig; Zurücksetzen stellt den erzeugten Wert wieder her.",
  "theme.entityColors": "Standardfarben",
  "theme.entityColors.description": "Grundlegende Ausweichfarben für Einträge — wähle sie auf der Farbtafel oben aus. Farben pro Thema, Kalender und Label findest du unter Einstellungen → Themen, Labels und Kalender.",
  "theme.uiColors": "Oberflächenfarben",
  "theme.uiColors.description": "Text, Menüs und Flächen — aus der Quellfarbe erzeugt, unabhängig von der Farbtafel.",
  "theme.entity": "Farben festlegen für",
  "theme.entity.description": "Alles, Aufgaben oder Termine — Farben pro Thema, Kalender und Label (unter Themen, Labels und Kalender) gewinnen über diese.",
  "theme.entity.global": "Alles",
  "theme.entity.task": "Aufgaben",
  "theme.entity.event": "Termine",
  "theme.paletteBoard": "Farbtafel",
  "theme.paletteBoard.applyTo": "Bemalt",
  "theme.paletteBoard.imageSample": "Bildpunkt",
  "theme.paletteBoard.debug": "Debug",
  "theme.paletteBoard.slot": "Palette {n}",
  "theme.paletteBoard.hint": "Wähle unten eine Farbe aus und klicke dann auf die Tafel — Kacheln wählen exakte Palettenfarben, das Kunstwerk beliebige Bildpunkte.",
  "theme.preset.change": "Design ändern",
  "theme.preset.hide": "Ausblenden",
  "theme.preview": "Vorschau",
  "theme.preview.day": "Tagesansicht",
  "theme.preview.overview": "Übersicht",
  "theme.mode.light": "Hell",
  "theme.mode.dark": "Dunkel",
  "theme.vibrancy": "Farbintensität",
  "theme.vibrancy.description": "Verstärke oder dämpfe die Sättigung aller Themen- und Kalenderfarben auf einmal.",
  "theme.autoColorCalendars": "Kalender automatisch einfärben",
  "theme.conflict.title": "Design auf einem anderen Gerät geändert",
  "theme.conflict.body": "Dieses Gerät und ein anderes haben unterschiedliche Designs. Das Design dieses Geräts behalten oder das synchronisierte verwenden?",
  "theme.conflict.keep": "Dieses Gerät behalten",
  "theme.conflict.override": "Synchronisiertes verwenden",
  "theme.variant.tonal_spot": "Tonal",
  "theme.variant.vibrant": "Lebhaft",
  "theme.variant.expressive": "Ausdrucksstark",
  "theme.variant.content": "Inhaltlich",
  "theme.variant.neutral": "Neutral",
  "settings.category.tasks": "Aufgaben & Kalender",
  "settings.category.tasks.blurb": "Vorgaben für neue Einträge, Erinnerungen, Unteraufgaben, Editor",
  "settings.category.navigation": "Navigation",
  "settings.category.navigation.blurb": "Seitenleiste und Tastenkürzel",
  "settings.category.navigation.mobileBlurb": "Seitenleiste",
  "settings.category.organize": "Themen, Labels und Kalender",
  "settings.category.organize.blurb": "Themen, Kalender, Labels — Farben, Reihenfolge, Sichtbarkeit",
  "settings.category.sync": "Sync & Konten",
  "settings.category.sync.blurb": "Konto, Geräte-Sync, Google",
  "settings.category.data": "Daten & Datenschutz",
  "settings.category.data.blurb": "API-Schlüssel, Import/Export, Backup",
  "settings.category.trash": "Papierkorb",
  "settings.category.trash.blurb": "Objekte wiederherstellen oder endgültig löschen",
  "settings.category.about": "Info & Feedback",
  "settings.category.about.blurb": "Version, Feedback, Entwickleroptionen",
  "settings.category.developer": "Entwickler",
  "settings.category.developer.blurb": "Diagnose, Debug-Flags, Fehlerlog",
  "settings.search.placeholder": "Einstellungen suchen...",
  "settings.search.none": "Keine passenden Einstellungen.",
  "settings.resetAria": "{title} auf Standard zurücksetzen",

  "general.appearance": "Darstellung",
  "general.languageTime": "Sprache & Zeit",
  "appearance.display": "Design & Farbe",
  "appearance.text": "Text",
  "general.theme": "Design",
  "general.theme.light": "Hell",
  "general.theme.dark": "Dunkel",
  "general.theme.system": "System",
  "general.blockColor": "Blockfarbe",
  "general.blockColor.description": "Kalender- und Eingangsblöcke nutzen die Aufgabenfarbe wie im Editor. Ziehe, um festzulegen, wie viel heller Aufgaben gegenüber Terminen wirken; eine Aufgabe mit Thema und eigener Farbe mischt beide.",
  "general.blockColor.aria": "Farbunterschied zwischen Aufgabe und Termin",
  "general.blockColor.gradient": "Verlauf bei Einzelfarben",
  "general.blockColor.task": "Aufgabe",
  "general.blockColor.event": "Termin",
  "general.blockColor.mix": "Mix",
  "general.display": "Anzeige",
  "general.language.description": "Steuert lokalisierte App-Oberflächen. Aktuell werden Englisch und Deutsch unterstützt.",
  "general.timeFormat": "Zeitformat",
  "general.timeFormat.24": "24 Stunden",
  "general.timeFormat.12": "12 Stunden",
  "general.textSize": "Textgröße",
  "general.textSize.description": "Skaliert App-Text, während Überschriften, Labels und kompakter Kalendertext proportional bleiben.",
  "general.textSize.aria": "App-Textgröße",
  "general.textSize.small": "Klein",
  "general.textSize.large": "Groß",
  "general.textSize.previewTitle": "Plan für morgen prüfen",
  "general.textSize.previewBody": "Überschriften, Fließtext und kompakte Labels behalten ihren eigenen Rhythmus.",
  "general.textFont": "Schriftart",
  "general.textFont.description": "Wähle die Hauptschrift, die in der App verwendet wird.",
  "general.textFont.system": "System",
  "general.textFont.rounded": "Rund",
  "general.textFont.serif": "Serif",
  "general.textFont.mono": "Mono",
  "general.startup": "Start",
  "general.defaultView": "Standardansicht",
  "general.defaultView.description": "Wo die App beim Start öffnet.",
  "general.multiDayFocus": "Fokus für Woche & X Tage",
  "general.multiDayFocus.description": "Wähle den ersten sichtbaren Tag, wenn Woche oder X Tage den aktuellen Zeitraum fokussiert. Aktuelle Woche folgt der Einstellung Woche beginnt am.",
  "general.multiDayFocus.currentWeek": "Aktuelle Woche",
  "general.multiDayFocus.currentDay": "Aktueller Tag",
  "general.multiDayFocus.previousDay": "Aktueller Tag − 1",
  "general.reset": "Zurücksetzen",
  "general.resetPrefs": "Alle Einstellungen zurücksetzen",
  "general.resetPrefs.description": "Setzt alle Einstellungen (Darstellung, Standardwerte, Schlummern usw.) zurück. Aufgaben, Kalender, Themen, Labels, Konto und Sync bleiben unverändert.",
  "general.resetPrefs.confirm": "Ja, Einstellungen zurücksetzen",
  "general.resetPrefs.button": "Alle Einstellungen zurücksetzen...",
  "general.resetPrefs.done": "Einstellungen wurden zurückgesetzt.",
  "general.showOnboarding": "Einrichtungsassistent",
  "general.showOnboarding.description": "Öffnet die Ersteinrichtung erneut: Konto, Benachrichtigungen, Import, Design, Organisation und Zeiteinstellungen.",
  "general.showOnboarding.button": "Einrichtungsassistent öffnen",

  "defaults.newEntries": "Neue Einträge",
  "defaults.newEntries.description": "Womit eine neue Aufgabe oder ein neuer Termin startet.",
  "defaults.defaultCalendar": "Standardkalender",
  "defaults.defaultCalendar.description": "Neue Einträge landen hier, sofern du nichts anderes auswählst.",
  "defaults.noCalendars": "(keine Kalender)",
  "defaults.defaultType": "Standardtyp",
  "defaults.defaultType.description": "Ob das Erstellen-Formular als Termin oder Aufgabe öffnet.",
  "defaults.defaultDuration": "Standard-Termindauer",
  "defaults.defaultDuration.description": "Länge eines neuen terminierten Eintrags über die +-Schaltfläche (Drag-to-create nutzt die gezogene Spanne).",
  "defaults.customDurationAria": "Eigene Standardlänge in Minuten",
  "defaults.timing": "Zeitplanung",
  "defaults.defaultTz": "Standard-Zeitzone",
  "defaults.defaultTz.description": "Wird auf neue Aufgaben angewendet. Eine andere Zone kannst du pro Aufgabe in den erweiterten Optionen wählen.",
  "defaults.weekStart": "Woche beginnt am",
  "defaults.sunday": "Sonntag",
  "defaults.monday": "Montag",
  "defaults.reminders.description": "Wird neuen Einträgen mitgegeben; pro Eintrag im Editor änderbar.",
  "defaults.taskReminders": "Standard-Erinnerungen für Aufgaben",
  "defaults.eventReminders": "Standard-Erinnerungen für Termine",
  "defaults.eventReminders.description": "Termine starten ohne Erinnerung; füge Vorlaufzeiten hinzu, wenn neue Termine ebenfalls erinnern sollen.",
  "defaults.snoozeAnchors": "Schlummer-Zeitpunkte",
  "defaults.snoozeAnchors.description": "Die Stunden, auf denen Schlummer-Vorgaben basieren. \"Morgen früh\" / \"Dieses Wochenende\" / \"Nächste Woche\" nutzen die Morgenstunde; \"Heute Abend\" nutzt die Abendstunde. Die Planungs-Schnellwahlen der Prioritäten-Ansicht nutzen alle drei.",
  "defaults.morning": "Morgen",
  "defaults.afternoon": "Nachmittag",
  "defaults.evening": "Abend",
  "defaults.snoozePreview": "Morgen früh -> {tomorrow} · Heute Abend -> {evening}",

  "taskSettings.remindersSnooze": "Erinnerungen & Schlummern",
  "taskSettings.subtasks": "Unteraufgaben",
  "taskSettings.todayView": "Heute-Ansicht",
  "taskSettings.agendaDays": "Agenda-Länge",
  "taskSettings.agendaDays.description": "Wie viele Tage die Heute-Ansicht vorausschaut und ob Tage ohne Einträge trotzdem angezeigt werden.",
  "taskSettings.agendaDays.option": "{count} Tage",
  "taskSettings.agendaShowEmpty": "Leere Tage",
  "taskSettings.subtaskCompletion": "Unteraufgaben-Abschluss",
  "taskSettings.subtaskCompletion.description": "Wie sich das Abschließen einer Aufgabe und ihrer Unteraufgaben gegenseitig beeinflusst.",
  "taskSettings.rollup.twoWay": "Beidseitig",
  "taskSettings.rollup.childrenToParent": "Kinder -> Eltern",
  "taskSettings.rollup.parentToChildren": "Eltern -> Kinder",
  "taskSettings.rollup.ask": "Jedes Mal fragen",
  "taskSettings.editor": "Editor",
  "taskSettings.matrixScope": "3×3-Eisenhower-Matrix (Priorität/Dringlichkeit)",
  "taskSettings.matrixScope.description": "Wo die 3×3-Matrix (Priorität × Dringlichkeit) verfügbar ist. Außerhalb ihres Bereichs nutzen die Formulare die klassischen Prioritätsflaggen und Blöcke behalten die Flaggenfarben.",
  "taskSettings.matrixScope.off": "Nicht aktivieren",
  "taskSettings.matrixScope.tasks": "Nur für Aufgaben",
  "taskSettings.matrixScope.tasksEvents": "Aufgaben & Termine",
  "taskSettings.deadlineUrgency": "Dringlichkeit aus Frist",
  "taskSettings.deadlineUrgency.description": "Wenn du eine Aufgabe nicht selbst in der Matrix platziert hast, wird ihre Dringlichkeit automatisch aus der Nähe der Frist bestimmt (ersatzweise aus dem geplanten Datum). Ausschalten, um die Auto-Dringlichkeit leer zu lassen, bis du sie selbst setzt.",
  "taskSettings.deadlineUrgency.on": "Auto",
  "taskSettings.deadlineUrgency.off": "Aus",
  "taskSettings.deadlineUrgency.highDays": "Hoch innerhalb (Tage)",
  "taskSettings.deadlineUrgency.mediumDays": "Mittel innerhalb (Tage)",
  "taskSettings.deadlineUrgency.thresholdHint": "≤ {high} Tage → hoch · ≤ {medium} Tage → mittel · darüber → niedrig. Heute zählt als 0 Tage; überfällig zählt als hoch.",
  "taskSettings.desktopFormVariant": "Editor-Stil (Desktop)",
  "taskSettings.desktopFormVariant.description": "Wie das Erstellen/Bearbeiten-Fenster auf dem Desktop aussieht. Papier ist die klassische Manuskript-Karte; Modern ist der neue Look im Stil des mobilen Designs — gleiches Layout, gleiche Felder und Animationen.",
  "taskSettings.desktopFormVariant.paper": "Papier (klassisch)",
  "taskSettings.desktopFormVariant.modern": "Modern (neu)",
  "taskSettings.topicEditor": "Themen erstellen/bearbeiten",
  "taskSettings.topicEditor.description": "Themen-Schaltflächen im Touch-Sheet können Icons, Text oder beides zeigen. Die Themen-pro-Aufgabe-Auswahl gilt auch für den Desktop/Web-Editor.",
  "taskSettings.topicButtons": "Themen-Schaltflächen",
  "taskSettings.iconsText": "Icons + Text",
  "taskSettings.icons": "Icons",
  "taskSettings.text": "Text",
  "taskSettings.topicsPerTask": "Themen pro Aufgabe",
  "taskSettings.multiple": "Mehrere",
  "taskSettings.single": "Einzeln",
  "taskSettings.mobile": "Mobil",
  "taskSettings.swipeHint": "Kalender-Wischhinweis",
  "taskSettings.swipeHint.description": "Zeigt oben in Kalenderansichten einen kleinen Hinweis: links zu Filtern, rechts zum Eingang. Ausblenden, sobald du die Gesten kennst.",

  "navigation.sidebar": "Seitenleiste",
  "navigation.showInSidebar": "In Seitenleiste anzeigen",
  "navigation.showInSidebar.description": "Blende Teile der Seitenleiste aus, die du nicht nutzt. (Der Titel \"YourCalendar\" führt immer nach Hause.)",
  "navigation.keyboardShortcuts": "Tastenkürzel",
  "navigation.part.today": "Heute",
  "navigation.part.today.desc": "Der Heute-Navigationspunkt.",
  "navigation.part.calendar": "Kalender",
  "navigation.part.calendar.desc": "Der Kalender-Navigationspunkt.",
  "navigation.part.tasks": "Alle Aufgaben",
  "navigation.part.tasks.desc": "Der Alle-Aufgaben-Navigationspunkt.",
  "navigation.part.priorities": "Prioritäten",
  "navigation.part.priorities.desc": "Die Wichtigkeit-×-Dringlichkeit-Entscheidungsansicht. Wird automatisch angezeigt, solange die Matrix aktiviert ist, sofern du dies nicht überschreibst.",
  "navigation.useAutomatic": "Automatisch verwenden",
  "navigation.part.inbox": "Eingang",
  "navigation.part.inbox.desc": "Der undatierte Aufgabenbestand.",
  "navigation.part.search": "Suche",
  "navigation.part.search.desc": "Der Such-Navigationspunkt.",
  "navigation.part.claude": "Claude",
  "navigation.part.claude.desc": "Der KI-Navigationspunkt.",
  "navigation.part.filters": "Filter",
  "navigation.part.filters.desc": "Die Gruppe Themen / Labels / Priorität / Kalender.",

  "shortcuts.title": "Tastenkürzel",
  "shortcuts.description": "Klicke auf eine Taste und drücke dann die neue Taste. Esc bricht ab.",
  "shortcuts.newTask": "Neue Aufgabe",
  "shortcuts.today": "Zu Heute",
  "shortcuts.priorities": "Zu Prioritäten",
  "shortcuts.calNext": "Kalender: nächster Zeitraum",
  "shortcuts.calPrev": "Kalender: vorheriger Zeitraum",
  "shortcuts.help": "Kürzelhilfe anzeigen",
  "shortcuts.undo": "Letzte Änderung rückgängig",
  "shortcuts.rebind": "{desc} neu belegen",
  "shortcuts.pressKey": "Taste drücken...",
  "shortcuts.duplicate": "Zwei Aktionen teilen sich eine Taste -- vergib eindeutige Tasten.",
  "shortcuts.showThisHelp": "Diese Hilfe anzeigen",
  "shortcuts.rebindHint": "Diese Kürzel kannst du unter Einstellungen -> Navigation ändern.",

  "data.integrations": "Integrationen",
  "data.apiKey": "Anthropic API-Schlüssel",
  "data.apiKey.description": "Wird lokal auf diesem Gerät gespeichert. Erforderlich für Claude-Funktionen.",
  "data.apiKey.saved": "Ein API-Schlüssel ist auf diesem Gerät gespeichert.",
  "data.apiKey.savedPlaceholder": "Neuen Schlüssel eingeben, um den gespeicherten zu ersetzen",
  "data.importExport": "Import & Export",
  "data.ics": "Kalender importieren/exportieren (.ics)",
  "data.ics.description": "Tausche Aufgaben, Termine und Wiederholungen mit anderen Kalender-Apps als iCalendar-Datei aus. Export lässt dich Kalender wählen; Import führt eine oder mehrere Dateien in einen gewählten Kalender zusammen und überspringt Vorhandenes.",
  "data.exportIcs": ".ics exportieren",
  "data.importIcs": ".ics importieren",
  "data.settingsFile": "Einstellungen exportieren/importieren",
  "data.settingsFile.description": "Speichere nur deine Einstellungen (Design, Standardwerte, Seitenleiste usw.) in eine Datei oder lade sie auf einem anderen Gerät. Enthält nie API-Schlüssel, Konto oder Aufgaben.",
  "data.exportSettings": "Einstellungen exportieren",
  "data.importSettings": "Einstellungen importieren",
  "data.backupRestore": "Backup & Wiederherstellung",
  "data.backupRestore.description": "Speichere alles (Aufgaben, Termine, Listen und Einstellungen -- nie deinen API-Schlüssel) in eine Datei, lade sie zurück oder lösche alle Daten auf diesem Gerät. Beim Laden kannst du Kategorien und Zusammenführen vs. Ersetzen wählen.",
  "data.saveToFile": "In Datei speichern",
  "data.loadFromFile": "Aus Datei laden",
  "data.deleteAllData": "Alle Daten löschen",
  "data.restore.clearing": "Vorhandene Daten werden gelöscht... {percent} %",
  "data.restore.writing": "Wird wiederhergestellt... {percent} %",

  "storage.fallback.warning": "Deine gespeicherten Daten konnten nicht geöffnet werden, deshalb ist die App leer gestartet. Es wurde nichts gelöscht -- bitte starte die App neu, bevor du etwas anlegst, und melde es, falls es erneut passiert.",

  "lists.calendars.description": "Jede Aufgabe gehört zu genau einem Kalender; ausgeblendete Kalender werden aus Ansichten herausgefiltert. Der Standardkalender wird neuen Aufgaben zugewiesen (pro Aufgabe im Editor unter Erweitert änderbar).",
  "lists.makeDefault": "Als Standard für neue Aufgaben festlegen",
  "lists.hiddenTitle": "Aus Ansichten ausgeblendet -- klicken zum Anzeigen",
  "lists.shownTitle": "In Ansichten sichtbar -- klicken zum Ausblenden",
  "lists.onlyCalendarTitle": "Der einzige Kalender kann nicht gelöscht werden",
  "lists.deleteCalendarTitle": "Kalender löschen",
  "lists.newCalendar": "Neuer Kalendername",
  "lists.deleteNamed": "\"{name}\" löschen?",
  "lists.deleteCalendarWithTasks": "Dies löscht den Kalender und seine {count} {noun} dauerhaft (einschließlich wiederkehrender Serien). Das kann nicht rückgängig gemacht werden.",
  "lists.deleteCalendarEmpty": "Dieser Kalender enthält keine Aufgaben. Löschen?",
  "lists.deleteCalendarAndTasks": "Kalender und {count} {noun} löschen",
  "lists.topics.description": "Verschachtelte Gruppierungen, die du Aufgaben zuweisen kannst (eine Aufgabe kann mehrere haben). Lege Icon (Emoji) und Farbe pro Thema fest -- beides erscheint auf den Themen-Tabs im Aufgabenformular. Ziehe den Griff einer Zeile nach rechts, um sie unter dem Thema darüber zu verschachteln, oder nach links, um sie wieder herauszuheben. Beim Löschen werden Unterthemen an das übergeordnete Thema gehängt.",
  "lists.topicIcon": "Icon von {name}",
  "lists.topicIconTitle": "Icon (Emoji), das auf dem Themen-Tab im Aufgabenformular erscheint -- klicken zum Ändern",
  "lists.deleteTopicTitle": "Thema löschen",
  "lists.newTopic": "Neuer Themenname",
  "lists.deleteTopicBody": "Unterthemen werden an das übergeordnete Thema gehängt. Aufgaben mit diesem Thema zeigen es nicht mehr an. Das kann nicht rückgängig gemacht werden.",
  "lists.labels.description": "Querschnitts-Tags, die du Aufgaben zuweisen kannst (eine Aufgabe kann mehrere haben) und in der Seitenleiste filtern kannst. Anders als Themen sind Labels nicht verschachtelt.",
  "lists.deleteLabelTitle": "Label löschen",
  "lists.newLabel": "Neuer Labelname",
  "lists.deleteLabelBody": "Aufgaben mit diesem Label zeigen es nicht mehr an. Das kann nicht rückgängig gemacht werden.",

  "organize.showIn": "Anzeigen in",
  "organize.showIn.month": "Monat",
  "organize.showIn.today": "Heute",
  "organize.showIn.priorities": "Prioritäten",
  "organize.showIn.shownTitle": "In {view} sichtbar — klicken zum Ausblenden",
  "organize.showIn.hiddenTitle": "In {view} ausgeblendet — klicken zum Anzeigen",
  "organize.globallyHidden": "global ausgeblendet",
  "organize.expandRow": "Ansichtsoptionen für {name} anzeigen",
  "organize.collapseRow": "Ansichtsoptionen für {name} ausblenden",
  "organize.dragRow": "Ziehen zum Umsortieren",
  "organize.paletteNumber": "#{n} — diese Nummer markiert seine Farbe auf dem Palettenbrett",
  "organize.colorTap": "Klicken, dann eine Farbe auf dem Palettenbrett darüber wählen",
  "organize.colorTapNamed": "Farbe von {name} -- klicken, dann eine Farbe auf dem Palettenbrett darüber wählen",
  "organize.colorArmed": "Auf Brett wählen",
  "organize.colorAuto": "Auto",
  "organize.colorCustom": "Eigene",
  "organize.colorRevert": "Zur automatischen Farbe zurückkehren",

  "emoji.pick": "Emoji auswählen",
  "emoji.pickNamed": "Emoji für {name} auswählen",
  "emoji.search": "Emoji in jeder Sprache suchen…",
  "emoji.frequent": "Vorschläge",
  "emoji.none": "Keine Ergebnisse",
  "emoji.remove": "Emoji entfernen",

  "backup.deleteAllShort": "Alles löschen",
  "backup.deleteAllTitle": "Alle Daten löschen",
  "backup.deleteAllBody": "Dies löscht alle Aufgaben, Termine, wiederkehrenden Serien, Labels, Themen und Kalender dauerhaft. Deine Einstellungen (API-Schlüssel, Präferenzen) bleiben erhalten, und ein frischer Standardkalender wird erstellt. Das kann nicht rückgängig gemacht werden.",
  "backup.deleteEverything": "Alles löschen",
  "backup.importTitle": "Aus Datei laden",
  "backup.importDescription": "Wähle aus, was aus diesem Backup übernommen werden soll.",
  "backup.settingsPreferences": "Einstellungen & Präferenzen",
  "backup.none": "keine",
  "backup.merge": "Zusammenführen -- nur markierte Elemente hinzufügen, die noch nicht vorhanden sind",
  "backup.replace": "Ersetzen -- markierte Kategorien überschreiben (andere bleiben unverändert)",

  "ics.exportTitle": "Kalender exportieren",
  "ics.exportDescription": "Wähle aus, welche Kalender in die .ics-Datei aufgenommen werden.",
  "ics.exportButton": "{count} {noun} exportieren",
  "ics.importTitle": "Kalender importieren",
  "ics.importSummary": "{count} {itemNoun} aus der gewählten {fileLabel}. Bereits vorhandene Einträge (per UID erkannt) werden übersprungen.",
  "ics.file": "Datei",
  "ics.files": "{count} Dateien",
  "ics.item": "Eintrag",
  "ics.items": "Einträge",
  "ics.createNewCalendar": "Neuen Kalender erstellen",
  "ics.calendarName": "Kalendername",
  "ics.addExistingCalendar": "Zu vorhandenem Kalender hinzufügen",

  "feedback.captureUnavailable": "Bildschirmaufnahme ist in diesem Browser nicht verfügbar.",
  "feedback.captureFailed": "Der Bildschirm konnte nicht aufgenommen werden.",
  "feedback.readyToShare": "Feedback ist bereit zum Teilen. Sende es an {email}.",
  "feedback.shareTabOpened": "Feedback-Tab mit Screenshot und E-Mail-Schritten geöffnet.",
  "feedback.emailDraftOpened": "E-Mail-Entwurf geöffnet.",
  "feedback.openFailed": "Feedback-Freigabe konnte nicht geöffnet werden.",
  "feedback.notes": "Notizen",
  "feedback.placeholder": "Was ist passiert? Was hast du erwartet?",
  "feedback.removeImage": "Entfernen",
  "feedback.previewAlt": "Vorschau des Feedback-Anhangs",
  "feedback.attachmentHint": "Hänge einen Screenshot oder ein Bild an, wenn es das Problem erklärt.",
  "feedback.capturing": "Nimmt auf...",
  "feedback.captureScreen": "Bildschirm aufnehmen",
  "feedback.attachImage": "Bild anhängen",
  "feedback.opening": "Öffnet...",
  "feedback.footer": "Feedback ist an {email} adressiert. Deine Mail- oder Teilen-App fragt nach, bevor etwas gesendet wird.",
  "feedback.disabled": "Screenshot-Feedback-Popup deaktiviert.",
  "feedback.hideAfterScreenshots": "Nach Screenshots nicht mehr anzeigen",
  "feedback.subject": "YourCalendar App-Feedback",
  "feedback.noNote": "(Keine Notiz angegeben.)",
  "feedback.openingTitle": "Feedback-E-Mail wird geöffnet",
  "feedback.openingBody": "Deine Mail-App wird geöffnet...",
  "feedback.composeTabTitle": "YourCalendar Feedback senden",
  "feedback.composeTabHeading": "YourCalendar Feedback senden",
  "feedback.composeTabIntro": "Dein Browser konnte den Screenshot nicht automatisch anhängen. Nutze diesen Tab, um Nachricht und Screenshot zu kopieren, und sende sie dann an {email}.",
  "feedback.openEmailApp": "Mail-App öffnen",
  "feedback.copyMessage": "Nachricht kopieren",
  "feedback.copyScreenshot": "Screenshot kopieren",
  "feedback.message": "Nachricht",
  "feedback.messageHelp": "Der Mail-App-Button füllt Empfänger, Betreff und Nachricht. Wenn deine Mail-App ohne Screenshot öffnet, kopiere den Screenshot hier und füge ihn dort ein oder hänge ihn an.",
  "feedback.screenshot": "Screenshot",
  "feedback.screenshotAlt": "Feedback-Screenshot",
  "feedback.statusEmailOpened": "Mail-App geöffnet. Füge den Screenshot ein oder hänge ihn an, falls er nicht automatisch übernommen wurde.",
  "feedback.statusMessageCopied": "Nachricht kopiert.",
  "feedback.statusMessageSelect": "Markiere den Nachrichtentext und kopiere ihn.",
  "feedback.statusScreenshotCopied": "Screenshot kopiert. Füge ihn in deine E-Mail ein.",
  "feedback.statusScreenshotCopyFailed": "Screenshot konnte nicht kopiert werden. Rechtsklicke auf den Screenshot und kopiere oder speichere ihn, dann hänge ihn an die E-Mail an.",

  "about.about": "Info",
  "about.sendFeedback": "Feedback senden",
  "about.sendFeedback.description": "Sende eine Notiz und optional ein Bild an {email}. Auf Mobilgeräten können System-Screenshots diesen Composer automatisch öffnen.",
  "about.screenshotFeedback": "Screenshot-Feedback",
  "about.screenshotFeedback.description": "Auf Mobilgeräten kann ein System-Screenshot den Feedback-Composer mit angehängtem Screenshot öffnen.",
  "about.advanced": "Erweitert",
  "about.developerOptions": "Entwickleroptionen",
  "about.developerOptions.description": "Schaltet die Entwickler-Kategorie frei -- Diagnose, Fehlerlog, Debug-Logging und Kalender-Pan-Tuning. Standardmäßig aus; für normale Nutzung nicht nötig.",
  "about.developerUnlocked": "Entwickleroptionen freigeschaltet -- siehe Entwickler-Kategorie.",

  "calendar.previousUnit": "Vorherige(r) {unit}",
  "calendar.nextUnit": "Nächste(r) {unit}",
  "calendar.swipeFilters": "<- Filter",
  "calendar.swipeInbox": "Eingang ->",
  "calendar.slideToExpand": "(zum Erweitern wischen)",
  "calendar.newEvent": "Neuer Termin",
  "calendar.fewerDays": "Weniger Tage",
  "calendar.moreDays": "Mehr Tage",
  "calendar.dayCount": "{count} Tage",

  "sidebar.hide": "{label} ausblenden",
  "sidebar.settings": "Seitenleisten-Einstellungen",
  "sidebar.expand": "{title} erweitern",
  "sidebar.collapse": "{title} einklappen",
  "sidebar.dragSection": "Abschnitt zum Neuordnen ziehen",
  "sidebar.manage": "Verwalten",
  "sidebar.manageTitle": "{title} verwalten",
  "sidebar.clear": "Zurücksetzen",
  "sidebar.any": "beliebig",
  "sidebar.all": "alle",
  "sidebar.matchAny": "Beliebige ausgewählte {noun} treffen",
  "sidebar.matchAll": "Alle ausgewählten {noun} treffen",
  "sidebar.label": "Label",
  "sidebar.topic": "Thema",
  "sidebar.priority": "Priorität",
  "sidebar.priority.high": "Hoch",
  "sidebar.priority.medium": "Mittel",
  "sidebar.priority.low": "Niedrig",
  "sidebar.priority.none": "Keine",
  "matrix.title": "Prioritätsmatrix",
  "matrix.importance": "Wichtigkeit",
  "matrix.urgency": "Dringlichkeit",
  "matrix.low": "niedrig",
  "matrix.high": "hoch",
  "matrix.hint": "Halten zum Feinjustieren",
  "matrix.clear": "Entfernen",
  "matrix.cellLabel": "Wichtigkeit {priority}, Dringlichkeit {urgency}",
  "matrix.axisOption": "{axis}: {level}",
  "matrix.autoUrgency": "Auto",
  "matrix.autoUrgency.hint": "Dringlichkeit folgt automatisch der Frist.",
  "matrix.manualUrgency": "Manuell",
  "matrix.manualUrgency.hint": "Dringlichkeit bleibt, wo du sie setzt.",
  "matrix.priorityFromMatrix": "Priorität folgt der Matrix",
  "matrix.hidden.off": "Im Board",
  "matrix.hidden.on": "Verborgen",
  "matrix.hidden.hide.hint": "Aus dem Prioritäten-Board ausblenden — für die ganze Serie. Der Eintrag belegt weiterhin Zeit im Kalender und in der Tagesauslastung.",
  "matrix.hidden.show.hint": "Wieder im Prioritäten-Board anzeigen — für die ganze Serie.",

  "priorities.title": "Prioritäten",
  "priorities.horizon.label": "Zeitraum",
  "priorities.horizon.today": "Heute",
  "priorities.horizon.d3": "3 Tage",
  "priorities.horizon.d7": "Woche",
  "priorities.horizon.d14": "2 Wochen",
  "priorities.horizon.d30": "Monat",
  "priorities.horizon.all": "Alle",
  "priorities.include.overdue": "Überfällig",
  "priorities.include.undated": "Ohne Datum",
  "priorities.include.done": "Erledigt",
  "priorities.include.events": "Termine",
  "priorities.summary": "{count} offen · {time} geplant",
  "priorities.summaryNoTime": "{count} offen",
  "priorities.corner.doNow": "Jetzt erledigen",
  "priorities.corner.schedule": "Zeit einplanen",
  "priorities.corner.squeeze": "Zwischendurch",
  "priorities.corner.later": "Vielleicht später",
  "priorities.board.hint": "Aufgabe ziehen, um die Priorität zu ändern — klicken zum Öffnen.",
  "priorities.board.empty": "Keine Aufgaben in diesem Zeitraum.",
  "priorities.tray.title": "Noch nicht eingeordnet ({count})",
  "priorities.tray.hint": "Auf das Brett ziehen, um sie einzuordnen.",
  "priorities.cluster.more": "+{count} weitere",
  "priorities.cluster.expand": "{count} weitere Aufgaben anzeigen",
  "priorities.cluster.collapse": "Aufgabengruppe einklappen",
  "priorities.unplace.hint": "Hier ablegen, um vom Brett zu nehmen — Wichtigkeit, Dringlichkeit und Priorität entfallen.",
  "priorities.queue.title": "Empfohlene Reihenfolge",
  "priorities.queue.empty": "In diesem Zeitraum gibt es nichts zu entscheiden.",
  "priorities.band.now": "Jetzt erledigen",
  "priorities.band.next": "Als Nächstes",
  "priorities.band.later": "Später",
  "priorities.band.unranked": "Unsortiert",
  "priorities.focusPick": "Fokus-Empfehlung",
  "priorities.strip.title": "Nächste 14 Tage",
  "priorities.strip.clear": "Ganzen Zeitraum zeigen",
  "priorities.strip.dropSchedule": "Zum Planen auf dem markierten Tag loslassen.",
  "priorities.strip.recurringDrop": "Diese Aufgabe wiederholt sich — verschiebe sie im Kalender.",
  "priorities.strip.capacity": "Tageskapazität",
  "priorities.strip.capacityOff": "Kein Limit",
  "priorities.ladder.title": "Planen für {day}",
  "priorities.ladder.keep": "Uhrzeit behalten",
  "priorities.ladder.allDay": "Ganztägig",
  "priorities.ladder.morning": "Morgens",
  "priorities.ladder.afternoon": "Nachmittags",
  "priorities.ladder.evening": "Abends",
  "priorities.ladder.exact": "Genaue Uhrzeit …",
  "priorities.ladder.commit": "Planen",
  "priorities.ladder.failed": "Die Aufgabe konnte nicht eingeplant werden.",
  "priorities.update.failed": "Der Eintrag konnte nicht aktualisiert werden.",
  "priorities.queue.dropBand": "Loslassen, um unter „{band}“ einzuordnen.",
  "priorities.queue.dropHere": "Aufgabe hier ablegen.",
  "priorities.stale.badge": "{weeks} Wo.",
  "priorities.stale.hint": "Seit {weeks} Wochen unangetastet — behalten, snoozen oder löschen?",
  "priorities.plan.button": "Tag planen",
  "priorities.plan.title": "{day} planen",
  "priorities.plan.intro": "Zeitvorschläge in den freien Lücken um bereits Geplantes. Abwählen, was nicht verschoben werden soll.",
  "priorities.plan.empty": "Nichts zu planen — an diesem Tag braucht keine offene Aufgabe eine Uhrzeit.",
  "priorities.plan.unfit": "Kein Platz mehr für:",
  "priorities.plan.apply": "{count} einplanen",
  "priorities.plan.applied": "{count} Aufgaben eingeplant",
  "priorities.plan.failed": "Die Planung konnte nicht abgeschlossen werden. Einige Aufgaben wurden möglicherweise bereits verschoben.",
  "priorities.dueNone": "Ohne Datum",
  "priorities.overdueToday": "Früher heute",
  "priorities.overdueBy": "{days} T. überfällig",
  "priorities.hide.action": "Aus der Matrix ausblenden",
  "priorities.hide.toast": "Aus der Matrix ausgeblendet.",
  "priorities.hidden.title": "Aus der Matrix ausgeblendet ({count})",
  "priorities.hidden.hint": "Diese Einträge belegen weiterhin Zeit im Kalender und in der Tagesauslastung — sie sind nur keine Entscheidungen. Ausblenden gilt immer für die ganze Serie.",
  "priorities.hidden.show": "Anzeigen",
  "priorities.enable.title": "Prioritätsmatrix aktivieren",
  "priorities.enable.body": "Bewerte Aufgaben nach Wichtigkeit und Dringlichkeit und erhalte eine empfohlene Reihenfolge. Aktiviere die Matrix, um Aufgaben auf dem Brett zu platzieren — Liste und Tagesleiste funktionieren auch ohne.",
  "priorities.enable.forTasks": "Für Aufgaben aktivieren",
  "priorities.enable.settings": "Einstellungen öffnen",
  "sidebar.filterPriority": "Nach Priorität {label} filtern",
  "sidebar.filteringPriority": "Filtert nach Priorität {label} -- klicken zum Entfernen",
  "sidebar.urgency": "Dringlichkeit",
  "sidebar.filterUrgency": "Nach Dringlichkeit {label} filtern",
  "sidebar.filteringUrgency": "Filtert nach Dringlichkeit {label} -- klicken zum Entfernen",
  "sidebar.urgency.none": "Nicht platziert",
  "sidebar.calendarState": "{name}-Kalender -- {state}",
  "sidebar.hiddenClickShow": "ausgeblendet, klicken zum Anzeigen",
  "sidebar.shownClickHide": "angezeigt, klicken zum Ausblenden",
  "sidebar.hiddenTitle": "Ausgeblendet -- klicken zum Anzeigen",
  "sidebar.shownTitle": "Angezeigt -- klicken zum Ausblenden",
  "sidebar.dragCalendar": "Kalender zum Neuordnen ziehen",
  "sidebar.filteringLabel": "Filtert nach diesem Label -- klicken zum Entfernen",
  "sidebar.filterByLabel": "Klicken, um nach diesem Label zu filtern",
  "sidebar.dragLabel": "Label zum Neuordnen ziehen",
  "sidebar.filteringTopic": "Filtert nach diesem Thema -- klicken zum Entfernen",
  "sidebar.filterByTopic": "Klicken, um nach diesem Thema zu filtern",
  "sidebar.dragTopic": "Thema zum Neuordnen ziehen",

  "aiQuickAdd.title": "Mit Claude hinzufügen",
  "aiQuickAdd.placeholder": "z. B. erinnere mich jeden Dienstag um 18 Uhr, Mama anzurufen",
  "aiQuickAdd.missingKey": "Lege zuerst deinen Anthropic-API-Schlüssel in den Einstellungen fest.",
  "aiQuickAdd.invalidResponse": "Claude hat unverständliche Erinnerungsdetails zurückgegeben. Formuliere die Erinnerung anders.",
  "aiQuickAdd.thinking": "Claude denkt nach…",

  "today.title": "Heute",
  "today.overdue": "Überfällig",
  "today.snoozed": "Schlummernd",
  "today.empty": "Heute ist nichts geplant.",
  "today.emptyDay": "Nichts geplant.",
  "today.wakes": "Weckt {time}",
  "today.wakeNow": "Jetzt wecken",
  "today.relative.today": "Heute",
  "today.relative.tomorrow": "Morgen",
  "today.greeting.morning": "Guten Morgen",
  "today.greeting.afternoon": "Guten Tag",
  "today.greeting.evening": "Guten Abend",
  "today.chip.tasks": "Offen",
  "today.chip.events": "Termine",
  "today.chip.done": "Erledigt",
  "today.upNext": "Als Nächstes",
  "today.happeningNow": "Läuft gerade",
  "today.startsIn": "in {duration}",
  "today.until": "bis {time}",
  "today.allClear": "Alles klar — jede Aufgabe für heute ist erledigt!",
  "today.dayDone": "Alle Aufgaben erledigt",
  "today.openCount": "{count} offen",
  "today.scheduledCount": "{count} geplant",
  "today.hideDone": "Erledigte ausblenden",
  "today.showDone": "Erledigte anzeigen",
  "today.showAllOverdue": "Alle {count} anzeigen",
  "today.showLess": "Weniger anzeigen",
  "today.moveToToday": "Auf heute verschieben",
  "today.movedToToday": "Auf heute verschoben",
  "today.moveFailed": "Die Aufgabe konnte nicht auf heute verschoben werden.",
  "today.rescheduled": "Aufgabe neu geplant",
  "today.rescheduleFailed": "Die Aufgabe konnte nicht neu geplant werden.",
  "today.snoozeFailed": "Die überfälligen Aufgaben konnten nicht geschlummert werden.",
  "today.overdueBy": "{days} T. überfällig",
  "today.overdueToday": "Früher heute",
  "today.now": "Jetzt",
  "today.weekOverview": "Agendaübersicht",
  "today.dayFree": "Frei",
  "today.chip.scheduled": "Verplant",
  "today.tomorrowFree": "Morgen ist frei.",
  "today.tomorrowPreview": "Morgen: {count} geplant · zuerst um {time}",
  "today.tomorrowPreviewNoTime": "Morgen: {count} geplant",
  "today.deadline": "Frist: {date}",
  "today.subtasks": "{done} von {total} Unteraufgaben erledigt",
  "today.rescheduleChip": "Zeit ändern",
  "today.openInCalendar": "Im Kalender öffnen",
  "today.planDay": "Meinen Tag planen",
  "today.chip.inbox": "Eingang",
  "today.sortAge": "Älteste zuerst",
  "today.sortPriority": "Nach Priorität",
  "today.bulkMoveAll": "Alle auf heute",
  "today.bulkSnoozeAll": "Alle schlummern",
  "today.bulkMoved": "{count} auf heute verschoben",
  "today.bulkMoveFailed": "Nicht alle überfälligen Aufgaben konnten auf heute verschoben werden.",
  "today.bulkSnoozed": "{count} bis morgen geschlummert",
  "today.collapseSection": "Bereich einklappen",
  "today.expandSection": "Bereich ausklappen",
  "today.moveModule": "Ziehen zum Anordnen",
  "today.moduleWidth": "Modulbreite",

  "agenda.allDay": "Ganztägig",
  "agenda.markDone": "Als erledigt markieren",
  "agenda.markNotDone": "Als offen markieren",
  "agenda.snoozeTitle": "Schlummern",
  "agenda.showOnCalendar": "Im Kalender zeigen",
  "agenda.clickToEdit": "Zum Bearbeiten klicken",
  "agenda.untitled": "(ohne Titel)",
  "agenda.recurring": "wiederholend",

  "tasks.title": "Alle Aufgaben",
  "tasks.empty": "Noch keine Aufgaben.",
  "tasks.due": "Fällig",
  "tasks.deadline": "Deadline",
  "tasks.duration": "Dauer",
  "tasks.priority": "Priorität",
  "tasks.urgency": "Dringlichkeit",
  "tasks.titleColumn": "Titel",
  "tasks.noDate": "Kein Datum",
  "tasks.deadlineAt": "Deadline {time}",
  "tasks.tapToEdit": "Tippen zum Bearbeiten",
  "tasks.doubleClickToEdit": "Doppelklick zum Bearbeiten",
  "tasks.markOpen": "Aufgabe öffnen",
  "tasks.markDone": "Aufgabe erledigen",

  "search.placeholder": "Aufgaben nach Titel, Notizen oder Ort suchen...",
  "search.hint": "Tippe, um alle Aufgaben zu durchsuchen.",
  "search.noMatch": "Keine Aufgaben passen zu \"{query}\".",
  "search.view": "Anzeigen",
  "list.showing": "Zeige {shown} von {total}",
  "list.showMore": "Mehr anzeigen",

  "inbox.description": "Undatierte Aufgaben. Ziehe sie auf eine Kalenderansicht, um sie als Zeitblock zu planen.",
  "inbox.empty": "Keine undatierten Aufgaben.",
  "inbox.panelEmpty": "Keine undatierten Einträge. Ziehe einen Block hierher, um ihn zu entschedulen, oder füge einen hinzu.",
  "inbox.longPressTitle": "Lange drücken, um in den Kalender zu ziehen",
  "inbox.dragTitle": "In den Kalender ziehen, um zu planen",
  "inbox.sort": "Sortieren",
  "inbox.sort.default": "Standard",
  "inbox.sort.priority": "Priorität",
  "inbox.sort.urgency": "Dringlichkeit",
  "inbox.sort.title": "Titel",
  "inbox.toggle": "Eingang umschalten",

  "datePicker.selectDate": "Datum auswählen",
  "datePicker.tomorrow": "Morgen",
  "datePicker.nextWeek": "Nächste Woche",

  "snooze.until": "Schlummern bis",
  "snooze.preset.evening": "Heute Abend",
  "snooze.preset.tomorrow": "Morgen früh",
  "snooze.preset.weekend": "Dieses Wochenende",
  "snooze.preset.week": "Nächste Woche",
  "snooze.custom": "Benutzerdefiniert...",
  "snooze.commit": "Schlummern",

  "recurring.deleteTitle": "Wiederholende(n) {noun} löschen",
  "recurring.editTitle": "Wiederholende(n) {noun} bearbeiten",
  "recurring.move": "\"{title}\" von {from} nach {to} verschieben?",
  "recurring.moveAllDay": "\"{title}\" von {from} auf ganztägig am {date} verschieben?",
  "recurring.resize": "Dauer von \"{title}\" auf {duration} setzen?",
  "recurring.delete": "\"{title}\" löschen?",
  "recurring.edit": "\"{title}\" bearbeiten?",
  "recurring.rename": "\"{title}\" in \"{nextTitle}\" umbenennen?",
  "recurring.partOfSeries": "Diese(r) {noun} ist Teil einer Serie.",
  "recurring.onlyThis": "Nur diese(n) {noun}",
  "recurring.thisAndFollowing": "Diese(n) und folgende {noun}s",
  "recurring.wholeSeries": "Ganze Serie",
  "recurring.all": "Alle {noun}s",

  "taskForm.task": "Aufgabe",
  "taskForm.event": "Termin",
  "taskForm.starts": "Start",
  "taskForm.ends": "Ende",
  "taskForm.deadline": "Deadline",
  "taskForm.duration": "Dauer",
  "taskForm.allDay": "Ganztägig",
  "taskForm.allDayLine1": "Ganz",
  "taskForm.allDayLine2": "Tag",
  "taskForm.reminder": "Erinnerung",
  "taskForm.repeating": "Wiederholung",
  "taskForm.noTopic": "Kein Thema",
  "taskForm.untitledTask": "Unbenannte Aufgabe...",
  "taskForm.untitledEvent": "Unbenannter Termin...",
  "taskForm.notesPlaceholder": "Notizen (Markdown unterstützt)",
  "taskForm.addNotes": "Notizen hinzufügen",
  "taskForm.markdownPreview": "Markdown-Vorschau",
  "taskForm.advancedOptions": "Erweiterte Optionen",
  "taskForm.markComplete": "Als erledigt markieren",
  "taskForm.markUncomplete": "Als unerledigt markieren",
  "taskForm.saving": "Speichert...",
  "taskForm.createTask": "Aufgabe erstellen",
  "taskForm.createEvent": "Termin erstellen",
  "taskForm.helper": "{mod} + Enter zum Speichern · Esc zum Abbrechen",
  "taskForm.switchToAllDay": "Zu ganztägig wechseln",
  "taskForm.durationOrAllDay": "Dauer oder ganztägig",
  "taskForm.labels": "Labels",
  "taskForm.noLabels": "Noch keine Labels -- füge welche in den Einstellungen hinzu.",
  "taskForm.timeZone": "Zeitzone",
  "taskForm.deviceLocal": "Gerät lokal (gleitend)",
  "taskForm.remindBeforeDue": "Erinnern (vor Fälligkeit)",
  "taskForm.pickDate": "Datum wählen",
  "taskForm.pickTime": "Uhrzeit wählen",
  "taskForm.selectTime": "Uhrzeit auswählen",
  "taskForm.clearTime": "Uhrzeit löschen",
  "taskForm.clearDate": "Datum löschen",
  "taskForm.clearDateTitle": "Datum löschen (im Eingang behalten)",
  "taskForm.startDateTime": "Startdatum und -zeit",
  "taskForm.endDateTime": "Enddatum und -zeit",
  "taskForm.setDate": "Datum setzen",
  "taskForm.setTime": "Uhrzeit setzen",
  "taskForm.endDate": "Enddatum",
  "taskForm.end": "Ende",
  "taskForm.addDeadline": "Deadline hinzufügen",
  "taskForm.clearDeadline": "Deadline löschen",
  "taskForm.editTask": "Aufgabe bearbeiten",
  "taskForm.newTask": "Neue Aufgabe",
  "taskForm.discardTitle": "Neuen Eintrag verwerfen?",
  "taskForm.discardCopy": "Dieses Formular enthält ungespeicherte Details. Weiter bearbeiten, um sie zu behalten, oder verwerfen und ohne Speichern schließen.",
  "taskForm.keepEditing": "Weiter bearbeiten",
  "taskForm.discard": "Verwerfen",
  "taskForm.discardChangesTitle": "Änderungen verwerfen?",
  "taskForm.discardMobileCopy": "Du hast ungespeicherte {kind}. Weiter bearbeiten, um sie zu behalten.",
  "taskForm.changes": "Änderungen",
  "taskForm.details": "Details",
  "taskForm.reopen": "Wieder öffnen",
  "taskForm.noPriority": "Keine Priorität",
  "taskForm.subtasks": "Unteraufgaben",
  "taskForm.subtask": "Unteraufgabe...",
  "taskForm.scheduled": "geplant",
  "taskForm.removeSubtask": "Unteraufgabe entfernen",
  "taskForm.addSubtask": "Unteraufgabe hinzufügen",
  "taskForm.location": "Ort",
  "taskForm.addLocation": "Ort hinzufügen",
  "taskForm.noMatches": "Keine Treffer",
  "taskForm.clear": "Löschen",
  "taskForm.deviceDefault": "Gerätestandard",
  "taskForm.chooseDifferentTopic": "Anderes Thema wählen",
  "taskForm.topic": "Thema",
  "taskForm.collapseTopics": "Themen einklappen",
  "taskForm.shorter": "Kürzer",
  "taskForm.longer": "Länger",

  "color.custom": "Benutzerdefinierte Farbe",
  "color.noColor": "Keine Farbe",
  "color.red": "Rot",
  "color.orange": "Orange",
  "color.yellow": "Gelb",
  "color.green": "Grün",
  "color.teal": "Türkis",
  "color.blue": "Blau",
  "color.purple": "Lila",
  "color.pink": "Pink",
  "color.lime": "Limette",
  "color.cyan": "Cyan",
  "color.indigo": "Indigo",
  "color.slate": "Schiefer",

  "reminder.remindMe": "Erinnere mich",
  "reminder.selectAny": "beliebig auswählen",
  "reminder.atTime": "Zur Zeit",
  "reminder.minBefore": "{count} Min. vorher",
  "reminder.hourBefore": "{count} Stunde vorher",
  "reminder.hoursBefore": "{count} Stunden vorher",
  "reminder.dayBefore": "Einen Tag vorher",
  "reminder.daysBefore": "{count} Tage vorher",
  "reminder.custom": "Benutzerdefiniert",
  "reminder.beforeStart": "vor Start",
  "reminder.remove": "Erinnerung entfernen",
  "reminder.minutes": "Minuten",
  "reminder.hours": "Stunden",
  "reminder.days": "Tage",

  "repeat.frequency": "Häufigkeit",
  "repeat.daily": "Täglich",
  "repeat.weekly": "Wöchentlich",
  "repeat.monthly": "Monatlich",
  "repeat.yearly": "Jährlich",
  "repeat.onDays": "An Tagen",
  "repeat.every": "Alle",
  "repeat.day": "Tag",
  "repeat.days": "Tage",
  "repeat.week": "Woche",
  "repeat.weeks": "Wochen",
  "repeat.month": "Monat",
  "repeat.months": "Monate",
  "repeat.year": "Jahr",
  "repeat.years": "Jahre",
  "repeat.endDate": "Enddatum",
  "repeat.seriesEndDate": "Serien-Enddatum",
  "repeat.endsOn": "Endet {date}",
  "repeat.noEndDate": "Kein Enddatum",
  "repeat.noEnd": "Kein Ende -- wiederholt sich fortlaufend",

  "sync.account": "Konto",
  "sync.emailUnverified": "Deine E-Mail ist noch nicht bestätigt. Prüfe deinen Posteingang.",
  "sync.resend": "Erneut senden",
  "sync.signedInAs": "Angemeldet als {email}",
  "sync.logOut": "Abmelden",
  "sync.deleteAccountDescription": "Konto und alle synchronisierten Daten dauerhaft vom Server löschen.",
  "sync.deleteAccount": "Konto löschen",
  "sync.signupDescription": "Erstelle ein Konto, um dieses Gerät mit deinen anderen zu synchronisieren.",
  "sync.loginDescription": "Melde dich an, um Aufgaben, Termine und Listen geräteübergreifend zu synchronisieren.",
  "sync.passwordSignup": "Passwort (min. 8 Zeichen)",
  "sync.password": "Passwort",
  "sync.createAccount": "Konto erstellen",
  "sync.signIn": "Anmelden",
  "sync.haveAccount": "Schon ein Konto? Anmelden",
  "sync.newHere": "Neu hier? Konto erstellen",
  "sync.forgotPassword": "Passwort vergessen?",
  "sync.enterEmailFirst": "Gib zuerst oben deine E-Mail ein.",
  "sync.deleteAccountBody": "Dies löscht dein Konto{email} und alle Daten auf dem Server dauerhaft. Das kann nicht rückgängig gemacht werden. Aufgaben auf diesem Gerät werden nicht entfernt -- nutze dafür \"Alle Daten löschen\".",
  "sync.deleteMyAccount": "Mein Konto löschen",
  "sync.multiDevice": "Geräte-Sync",
  "sync.multiDevice.description": "Deine Aufgaben, Termine und Listen bleiben auf allen angemeldeten Geräten synchron. Änderungen werden feldweise zusammengeführt, Löschungen übertragen. Melde dich oben mit dem Konto an, um Sync zu aktivieren.",
  "sync.syncing": "Synchronisiert...",
  "sync.syncNow": "Jetzt synchronisieren",
  "sync.signInToSync": "Zum Synchronisieren anmelden",
  "sync.setServer": "Sync-Server unter Erweitert setzen",
  "sync.lastSynced": "Zuletzt synchronisiert {time}",
  "sync.enabled": "Sync aktiviert",
  "sync.advancedServer": "Erweitert: Sync-Server",
  "sync.serverDescription": "Standard ist der gehostete Server. Ändere dies nur, wenn du einen eigenen betreibst.",
  "sync.saveServer": "Server-URL speichern",
  "sync.googleCalendar": "Google Kalender",
  "sync.googleNotConfigured": "Google-Kalender-Sync ist in diesem Build nicht aktiviert (keine Client-ID konfiguriert).",
  "sync.googleConnectDescription": "Verbinde dein Google-Konto, um Termine in beide Richtungen zu synchronisieren, solange die App geöffnet ist.",
  "sync.connecting": "Verbindet...",
  "sync.connectGoogle": "Google Kalender verbinden",
  "sync.continueWithGoogle": "Mit Google fortfahren",
  "sync.signedInWithGoogle": "Mit Google angemeldet.",
  "sync.googleSeparateNote": "Das Verbinden von Google Kalender ist getrennt von der Anmeldung — es fügt nur die beidseitige Kalendersynchronisierung hinzu (im Web ist ein Konto-/Sync-Server nötig).",
  "onboarding.title": "Willkommen bei YourCalendar",
  "onboarding.subtitle": "Wähle, wie du starten möchtest — du kannst das später in den Einstellungen ändern.",
  "onboarding.accountNote": "Synchronisiere Aufgaben, Termine & Listen geräteübergreifend, mit Online-Backup.",
  "onboarding.createAccount": "Konto erstellen",
  "onboarding.signInInstead": "Schon ein Konto? Anmelden",
  "onboarding.continueWithGoogle": "Mit Google fortfahren",
  "onboarding.continueWithout": "Ohne Konto fortfahren",
  "onboarding.withoutNote": "Kein Online-Backup und keine Synchronisierung zwischen Geräten — alles bleibt auf diesem Gerät.",
  "onboarding.back": "Zurück",
  "onboarding.intro": "Wähle jetzt das Wichtigste aus. Alles hier bleibt in den Einstellungen änderbar.",
  "onboarding.skip": "Einrichtung überspringen",
  "onboarding.stepAccount": "Konto",
  "onboarding.stepNotifications": "Benachrichtigungen",
  "onboarding.stepImport": "Import & Sync",
  "onboarding.stepTheme": "Design",
  "onboarding.stepOrganization": "Organisation",
  "onboarding.stepTime": "Sprache & Zeit",
  "onboarding.notifTitle": "Erinnerungs-Benachrichtigungen",
  "onboarding.notifBody": "Sende jetzt eine Test-Benachrichtigung, damit Erinnerungen die nötige Berechtigung anfragen können.",
  "onboarding.notifTest": "Test-Benachrichtigung senden",
  "onboarding.notifTesting": "Wird getestet...",
  "onboarding.notifTested": "Test gesendet.",
  "onboarding.notifScheduled": "Benachrichtigung beim System eingeplant.",
  "onboarding.notifSent": "Test-Benachrichtigung gesendet.",
  "onboarding.importIcsTitle": ".ics-Kalender importieren",
  "onboarding.importIcsBody": "Übernimm Termine und wiederkehrende Einträge aus einer iCalendar-Datei.",
  "onboarding.importing": "Wird importiert...",
  "onboarding.googleBody": "Verbinde Google Kalender und wähle die zu koppelnden Kalender nach der Einrichtung.",
  "onboarding.connected": "Verbunden",
  "onboarding.topicsBody": "Deine Themen, genau wie in den Einstellungen: Ziehe am Griff zum Sortieren, ziehe eine Zeile nach rechts, um sie als Unterthema einzunisten (nach links zum Hochstufen), klicke auf das Emoji-Feld für ein Symbol.",
  "onboarding.labelsBody": "Bearbeite übergreifende Tags für Priorität, Status oder Kontext.",
  "onboarding.themePreviewTitle": "Live-Vorschau",
  "onboarding.themePreviewBody": "Eine Woche voller Beispielaufgaben im oben gewählten Design — wähle ein Preset und sieh zu, wie sich alles sofort umfärbt.",
  "onboarding.webAvailableTitle": "Auch im Web verfügbar",
  "onboarding.webAvailableBody": "Nutze YourCalendar in jedem Browser unter yourcalendar.app.",
  "onboarding.openWebApp": "yourcalendar.app öffnen",
  "onboarding.appAvailableTitle": "Auch als Android-App verfügbar",
  "onboarding.appAvailableBody": "Installiere YourCalendar über Google Play als vollständige mobile App.",
  "onboarding.openPlayStore": "Bei Google Play herunterladen",
  "sync.connectedAs": "Verbunden als {email}",
  "sync.googleAccount": "Google-Konto",
  "sync.disconnect": "Trennen",
  "sync.loadingCalendars": "Kalender werden geladen...",
  "sync.primary": "(primär)",
  "sync.twoWay": "Beidseitig",
  "sync.googleToApp": "Google -> App",
  "sync.appToGoogle": "App -> Google",
  "sync.paused": "Pausiert",
  "sync.addToApp": "Zur App hinzufügen",

  "trash.description": "Gelöschte Aufgaben und Termine. Stelle sie wieder her oder entferne sie endgültig.",
  "trash.emptyTrash": "Papierkorb leeren",
  "trash.empty": "Der Papierkorb ist leer. Gelöschte Aufgaben und Termine erscheinen hier.",
  "trash.deletedAt": "gelöscht {time}",
  "trash.deletePermanently": "Endgültig löschen",
  "trash.emptyConfirm": "Dies löscht alle {count} Objekt(e) im Papierkorb endgültig. Das kann nicht rückgängig gemacht werden.",
  "trash.deleteConfirm": "\"{title}\" wird endgültig gelöscht. Das kann nicht rückgängig gemacht werden.",

  "rollup.complete": "Erledigen",
  "rollup.reopen": "Wieder öffnen",
  "rollup.completed": "erledigt",
  "rollup.reopened": "wieder geöffnet",
  "rollup.title": "{verb} mit Unteraufgaben?",
  "rollup.body": "\"{title}\" hat verwandte Aufgaben. Sollen sie ebenfalls {what} werden?",
  "rollup.cascadeDown": "{verb}: diese und alle Unteraufgaben",
  "rollup.cascadeUp": "{verb}: diese und die Elternaufgabe",
  "rollup.onlyThis": "Nur diese(n) {noun}",

  "resetPassword.title": "Passwort zurücksetzen",
  "resetPassword.missingToken": "Diesem Link fehlt der Token. Fordere in der App eine neue Zurücksetzen-E-Mail an.",
  "resetPassword.done": "Dein Passwort wurde aktualisiert. Melde dich mit dem neuen Passwort an.",
  "resetPassword.openApp": "YourCalendar öffnen",
  "resetPassword.newPassword": "Neues Passwort (mind. 8 Zeichen)",
  "resetPassword.confirmPassword": "Neues Passwort bestätigen",
  "resetPassword.mismatch": "Passwörter stimmen nicht überein.",
  "resetPassword.submit": "Neues Passwort setzen",

  "demos.replay": "Wiederholen",
  "demos.pause": "Pausieren",
  "demos.resume": "Fortsetzen",
  "demos.loading": "Demo wird geladen…",
  "demos.failed": "Die Demo kann hier nicht angezeigt werden. Alles Gezeigte funktioniert in der App ganz normal.",
  "demos.title.basicsReschedule": "Ziehen zum Verschieben",
  "demos.cap.reschedule.intro": "Um eine Aufgabe zu verschieben, ziehe einfach ihren Block…",
  "demos.cap.reschedule.drag": "…auf einen neuen Tag und eine neue Uhrzeit.",
  "demos.cap.reschedule.moved": "Losgelassen — die Aufgabe liegt jetzt im neuen Zeitfenster.",
  "demos.cap.reschedule.complete": "Ein Tipp auf die Checkbox erledigt sie.",
  "demos.fx.standup": "Team-Standup",
  "demos.fx.writeReport": "Bericht schreiben",
  "demos.fx.designReview": "Design-Review",
  "demos.fx.lunch": "Mittagessen mit Alex",
  "demos.fx.gym": "Fitnessstudio",
  "demos.nestDemoTitle": "Arbeit in Unteraufgaben bündeln",
  "demos.nestDemoBody": "Jede Aufgabe kann in einer anderen leben: Lass sie auf dem Titel eines Blocks los, um sie einzunisten.",
  "demos.title.basicsNest": "Auf einen Block ziehen zum Einnisten",
  "demos.cap.nest.intro": "Eine Aufgabe kann zur Unteraufgabe eines anderen Blocks werden…",
  "demos.cap.nest.dwell": "…halte sie einen Moment über dem Titel des Ziels…",
  "demos.cap.nest.nested": "…und sie nistet sich direkt im Kalender in ihre Elternaufgabe ein.",
  "demos.fx.planTrip": "Sommerreise planen",
  "demos.fx.bookHotel": "Hotel buchen",
  "demos.title.matrixScope": "Flaggen vs. Prioritätsmatrix",
  "demos.cap.matrixScope.flags": "Matrix aus: Aufgaben UND Termine tragen eine klassische Prioritätsflagge — drei feste Farben.",
  "demos.cap.matrixScope.matrixOn": "Umgestellt auf „Nur für Aufgaben“: Jede Aufgabe wandert auf die 3×3-Matrix — Priorität ist jetzt Wichtigkeit × Dringlichkeit.",
  "demos.cap.matrixScope.diagonal": "Jede Matrix-Position bekommt ihre eigene, stufenlose Farbe entlang der Grün→Rot-Diagonale.",
  "demos.cap.matrixScope.eventsKeepFlags": "Der Mittagessen-TERMIN blieb unverändert — im Modus „Nur für Aufgaben“ behalten Termine ihre klassische Flagge.",
  "demos.cap.matrixScope.eventsToo": "Umgestellt auf „Aufgaben & Termine“: Jetzt nutzen auch Termine die Matrix.",
  "demos.cap.matrixScope.eventPlaced": "Der Termin bekommt seinen eigenen Platz — dringend, aber nicht wichtig, also wandert seine Farbe Richtung Orange.",
  "demos.fx.errands": "Besorgungen machen",
  "demos.fx.prepSlides": "Folien vorbereiten",
  "demos.fx.quarterlyReport": "Quartalsbericht",
  "demos.title.deadlineUrgency": "Dringlichkeit folgt der Frist",
  "demos.cap.deadline.intro": "Bei automatischer Dringlichkeit misst die Dringlichkeit, wie nah die Frist einer Aufgabe an heute liegt.",
  "demos.cap.deadline.today": "Diese erste Spalte ist heute — der Bezugstag, von dem aus gemessen wird.",
  "demos.cap.deadline.far": "Diese Aufgabe ist in 4 Tagen fällig — Dringlichkeit: niedrig.",
  "demos.cap.deadline.drag": "Zieh sie näher an heute heran…",
  "demos.cap.deadline.medium": "Jetzt in 2 Tagen fällig — die Dringlichkeit steigt auf mittel.",
  "demos.cap.deadline.high": "Morgen fällig — die Dringlichkeit springt auf hoch.",
  "demos.cap.deadline.tracked": "Matrix-Spalte und Blockfarbe folgen der Frist automatisch — jeden Tag, ganz von selbst.",
  "demos.fx.taxes": "Steuererklärung abgeben",
  "demos.title.subtaskRollup": "Erledigung mit Unteraufgaben",
  "demos.title.todayTour": "Die Heute-Ansicht",
  "demos.cap.today.intro": "Heute bündelt deinen ganzen Tag — Zeitplan, Fortschritt und alles Überfällige.",
  "demos.cap.today.complete": "Aufgaben direkt in der Liste abhaken.",
  "demos.cap.today.overdue": "Verpasstes sammelt sich unter Überfällig.",
  "demos.cap.today.moveToday": "Ein Tipp holt es in den heutigen Plan.",
  "demos.cap.today.snooze": "Nicht heute? Schlummern — es wartet still bis morgen.",
  "demos.fx.reviewBudget": "Budget prüfen",
  "demos.fx.teamLunch": "Team-Mittagessen",
  "demos.fx.draftReport": "Bericht entwerfen",
  "demos.fx.callBank": "Bank anrufen",
  "demos.cap.rollup.twoWay": "Beidseitig: Hake die Unteraufgaben nacheinander ab…",
  "demos.cap.rollup.childrenDone": "…alle Unteraufgaben erledigt — die Elternaufgabe erledigt sich selbst.",
  "demos.cap.rollup.parentMode": "Modus Eltern → Kinder: Erledige jetzt stattdessen die Elternaufgabe…",
  "demos.cap.rollup.parentDone": "…und jede Unteraufgabe zieht mit.",
  "demos.fx.renovation": "Flur renovieren",
  "demos.fx.paint": "Farbe kaufen",
  "demos.fx.orderFloor": "Bodenbelag bestellen",
  "demos.title.themePreview": "Live-Kalendervorschau",
  "demos.topic.work": "Arbeit",
  "demos.topic.family": "Familie",
  "demos.topic.health": "Gesundheit",
  "demos.topic.home": "Zuhause",
  "demos.fx.dinner": "Abendessen mit Freunden",
  "demos.fx.hike": "Morgenwanderung",
  "demos.showMe": "Zeig mir das",
  "demos.hideDemo": "Demo ausblenden",
  "demos.compare.flagsTitle": "Prioritätsflaggen",
  "demos.compare.flagsNote": "Drei feste Stufen — jeder Eintrag ist genau niedrig, mittel oder hoch.",
  "demos.compare.matrixTitle": "3×3-Prioritätsmatrix",
  "demos.compare.matrixNote": "Priorität ist die Diagonale aus Wichtigkeit × Dringlichkeit — zieh den Punkt und beobachte den stufenlosen Farbverlauf.",
  "demos.compare.usedFor": "Gilt für:",
  "demos.compare.notUsed": "In diesem Modus ungenutzt",
  "demos.compare.chip.tasks": "Aufgaben",
  "demos.compare.chip.events": "Termine",
  "demos.compare.summary.off": "Aktueller Modus: Aufgaben und Termine nutzen beide klassische Prioritätsflaggen — die Matrix ist aus.",
  "demos.compare.summary.tasks": "Aktueller Modus: Aufgaben nutzen die 3×3-Matrix; Termine behalten klassische Prioritätsflaggen.",
  "demos.compare.summary.tasksEvents": "Aktueller Modus: Aufgaben und Termine nutzen beide die 3×3-Matrix.",
  "demos.rail.matrixOff": "Matrix aus — klassische Flaggen",
  "widget.nextUpEmpty": "Alles frei",
  "widget.focusTitle": "Top-Prioritäten",
  "widget.focusEmpty": "Keine offenen Prioritäten",
  "widget.overdue": "{count} überfällig",

  "contextMenu.markComplete": "Als erledigt markieren",
  "contextMenu.markUncomplete": "Als offen markieren",
  "contextMenu.wakeNow": "Jetzt aufwecken",
  "contextMenu.snooze": "Schlummern...",
  "contextMenu.createTaskHere": "Aufgabe hier anlegen",
  "contextMenu.duplicateTask": "Aufgabe duplizieren",
  "contextMenu.duplicateEvent": "Termin duplizieren",
  "contextMenu.moveToParent": "Zur übergeordneten Aufgabe verschieben",
  "calendar.dragToMove": "Ziehen, um die Startzeit zu ändern",
  "calendar.dragToResize": "Ziehen, um die Dauer zu ändern",
  "calendar.snoozedLabel": "schlummert",
  "calendar.createTask": "Aufgabe anlegen",
  "calendar.insert": "Einfügen",
  "calendar.showLess": "Weniger anzeigen",
  "calendar.subtaskRowHint": "Klicken zum Bearbeiten - ziehen zum Verschieben oder Umsortieren",
  "sidebar.resize": "Seitenleiste anpassen",
  "common.decrease": "Verringern",
  "common.increase": "Erhöhen",
  "theme.previewMuted": "Aa - gedämpfter Text - sekundäre Flächen",
  "sync.googleEncoding.tasks": "Aufgaben werden ebenfalls als Termine synchronisiert: Eine Aufgabe erscheint als \"⬜ Titel\" (erledigt → \"✅\"). Tausche ⬜/✅, um sie zu erledigen, oder schreibe \"[ ]\" bzw. \"Task:\" vor einen Titel, um einen Termin in eine Aufgabe zu verwandeln.",
  "sync.googleEncoding.subtasks": "Teilaufgaben stehen in der Beschreibung als ☐/☑. Zum Abhaken ersetze das Kästchen durch \"[x]\"; für eine neue Teilaufgabe füge eine Zeile \"[ ] Teilaufgabe\" hinzu.",
  "ai.dailyDigest": "Tagesüberblick",
  "ai.dailyDigest.generate": "Erstellen",
  "ai.dailyDigest.generating": "Wird erstellt…",
  "ai.dailyDigest.refresh": "Aktualisieren",
  "ai.dailyDigest.empty": "Klicke auf Erstellen, um einen kurzen Überblick über deinen Tag zu erhalten.",
  "color.pickCustom": "Eigene Farbe wählen",
  "notification.fallbackTitle": "Erinnerung",
  "notification.due": "Fällig {time}",
  "notification.bundleTitle": "{count} Erinnerungen",
  "notification.channelName": "Erinnerungen",
  "notification.channelDescription": "Erinnerungen für Aufgaben und Termine",
  "notification.snooze10": "10 Min schlummern",
  "notification.snooze60": "1 Std schlummern",
  "notification.testTitle": "Testbenachrichtigung",
  "notification.testBackground": "Geplanter Test ausgelöst - Zustellung im Hintergrund funktioniert.",
  "notification.testImmediate": "Sofort-Test - Benachrichtigungen funktionieren.",
  "notification.notPermitted": "Benachrichtigungen sind nicht erlaubt. Aktiviere sie in den Android-Einstellungen > Apps > YourCalendar > Benachrichtigungen.",

  "common.conflict": "Konflikt",
  "common.conflicts": "Konflikte",
  "toast.genericError": "Etwas ist schiefgelaufen. Falls das öfter passiert, lade die Seite neu.",
  "toast.file.invalidJson": "Diese Datei ist kein gültiges JSON.",
  "toast.server.waking": "Server wird aufgeweckt -- die erste Verbindung nach längerer Pause kann bis zu einer Minute dauern...",
  "toast.backup.saved": "Backup gespeichert.",
  "toast.backup.failed": "Backup fehlgeschlagen: {error}",
  "toast.backup.notValid": "Keine gültige Backup-Datei: {error}",
  "toast.backup.restored": "Backup wiederhergestellt.",
  "toast.backup.merged": "Backup zusammengeführt.",
  "toast.backup.restoreFailed": "Wiederherstellen fehlgeschlagen: {error}",
  "toast.data.deleted": "Alle Daten gelöscht.",
  "toast.data.deleteFailed": "Löschen fehlgeschlagen: {error}",
  "toast.settings.noneInFile": "In dieser Datei wurden keine Einstellungen gefunden.",
  "toast.settings.imported": "Einstellungen importiert.",
  "toast.ics.nothingToExport": "Für diese Auswahl gibt es nichts zu exportieren.",
  "toast.ics.exported": "{count} {noun} exportiert (.ics).",
  "toast.ics.exportFailed": "Export fehlgeschlagen: {error}",
  "toast.ics.noneImportable": "In den gewählten Dateien wurden keine importierbaren Termine gefunden.",
  "toast.ics.imported": "{count} {noun} importiert.",
  "toast.ics.importedSkipped": "{count} {noun} importiert ({skipped} bereits vorhanden).",
  "toast.ics.importFailed": "Import fehlgeschlagen: {error}",
  "toast.sync.setUrlFirst": "Lege zuerst eine Sync-Server-URL fest.",
  "toast.sync.signInFirst": "Melde dich an, um zu synchronisieren.",
  "toast.sync.done": "Synchronisiert (runter {pulled}, hoch {pushed}).",
  "toast.sync.doneWithConflicts": "Synchronisiert (runter {pulled}, hoch {pushed}); {count} {noun} aufgelöst.",
  "toast.sync.failed": "Synchronisierung fehlgeschlagen: {error}",
  "toast.trash.taskRestored": "Aufgabe wiederhergestellt",
  "toast.trash.eventRestored": "Termin wiederhergestellt",
  "toast.undo.done": "Rückgängig gemacht.",
  "toast.undo.failed": "Rückgängig fehlgeschlagen: {error}",
  "toast.task.added": "Aufgabe hinzugefügt",
  "toast.task.created": "Aufgabe erstellt",
  "toast.task.updated": "Aufgabe aktualisiert",
  "toast.task.completed": "Aufgabe erledigt",
  "toast.task.reopened": "Aufgabe wieder geöffnet",
  "toast.task.deleted": "Aufgabe gelöscht",
  "toast.task.woken": "Aufgabe aufgeweckt",
  "toast.task.duplicated": "Aufgabe dupliziert",
  "toast.task.eventDuplicated": "Termin dupliziert",
  "toast.task.snoozedUntil": "Schlummert bis {when}",
  "toast.task.subtasksReordered": "Teilaufgaben neu sortiert",
  "toast.task.occurrenceUpdated": "Termin der Serie aktualisiert",
  "toast.task.occurrenceRescheduled": "Termin der Serie verschoben",
  "toast.task.occurrenceResized": "Dauer des Serientermins geändert",
  "toast.task.occurrenceCompleted": "Serientermin erledigt",
  "toast.task.occurrenceReopened": "Serientermin wieder geöffnet",
  "toast.task.seriesRescheduled": "Serie verschoben",
  "toast.task.seriesResized": "Dauer der Serie geändert",
  "toast.task.seriesUpdated": "Serie aktualisiert",
  "toast.task.seriesEndChanged": "Serienende geändert",
  "toast.task.deletedFromSeries": "Gelöscht",
  "toast.auth.googleNotConfigured": "Google-Anmeldung ist in diesem Build nicht konfiguriert.",
  "toast.auth.googleSignedIn": "Mit Google angemeldet.",
  "toast.auth.accountCreated": "Konto erstellt.",
  "toast.auth.signedIn": "Angemeldet.",
  "toast.auth.accountDeleted": "Konto gelöscht.",
  "toast.auth.resetLinkSent": "Falls es zu dieser E-Mail ein Konto gibt, ist ein Link zum Zurücksetzen unterwegs.",
  "toast.auth.passwordUpdated": "Passwort aktualisiert. Melde dich mit deinem neuen Passwort an.",
  "toast.auth.verificationSent": "Bestätigungs-E-Mail gesendet.",
  "toast.google.notConfigured": "Google Kalender ist in diesem Build nicht konfiguriert.",
  "toast.google.setUrlFirst": "Lege zuerst eine Sync-Server-URL fest (Einstellungen -> Sync) -- die Web-Google-Synchronisierung tauscht Tokens darüber aus.",
  "toast.google.noRefreshToken": "Google hat kein Refresh-Token zurückgegeben. Entziehe der App den Zugriff auf myaccount.google.com und verbinde sie neu.",
  "toast.google.connectedAs": "{email} verbunden.",
  "toast.google.connected": "Google Kalender verbunden.",
  "toast.google.syncFailed": "Google-Kalender-Synchronisierung fehlgeschlagen: {error}",
  "toast.google.syncedWithFailures": "Synchronisiert -- {count} {noun} konnten nicht gesendet werden (übersprungen).",
  "toast.google.synced": "Google Kalender synchronisiert.",
  "toast.google.conflictsResolved": "{count} {noun} aufgelöst (Googles Version behalten).",
};

const TRANSLATIONS: Record<AppLanguage, Record<I18nKey, string>> = {
  en: EN,
  de: DE,
};

export function translate(
  language: AppLanguage,
  key: I18nKey,
  vars?: Record<string, string | number | null | undefined>,
): string {
  const template = (TRANSLATIONS[language] ?? TRANSLATIONS[DEFAULT_LANGUAGE])[key] ?? EN[key];
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, name: string) => String(vars[name] ?? ""));
}
